// @ts-nocheck
import { Component } from '@theme/component';
import {
  getStoreStatus as sharedGetStoreStatus,
  calculateStoreStatus as sharedCalculateStoreStatus,
  isStoreOpen as sharedIsStoreOpen,
  getStoreTimezone as sharedGetStoreTimezone,
  getStoreCurrentDayOfWeek as sharedGetStoreCurrentDayOfWeek,
  getNextOpeningTime as sharedGetNextOpeningTime,
  fmtTime as sharedFmtTime
} from './store-status.js';

// Debounce function
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

/**
 * Store Locator Component
 * Manages store selection and display modals
 */
export class StoreLocatorComponent extends Component {
  // Database constants
  static DB_NAME = 'StoreLocatorDB';
  static DB_VERSION = 2;
  static PREFERRED_STORE_KEY = 'preferred_store';
  static CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes TTL

  /**
   * Compute SHA-256 hash of data (with fallback)
   * @param {any} data
   * @returns {Promise<string>}
   */
  static async computeHash(data) {
    if (!data) return '';
    try {
      const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
      if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
        const encoder = new TextEncoder();
        const buf = await window.crypto.subtle.digest('SHA-256', encoder.encode(jsonString));
        return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
      }
    } catch (_) { /* fallback below */ }
    const str = typeof data === 'string' ? data : JSON.stringify(data);
    let h = 0;
    for (let i = 0; i < str.length; i++) { h = (h << 5) - h + str.charCodeAt(i); h |= 0; }
    return 'fb_' + Math.abs(h).toString(16);
  }

  async computeHash(data) {
    return StoreLocatorComponent.computeHash(data);
  }

  constructor() {
    super();
    // Internal flags/observer for header DOM changes
    this._headerObserver = null;
    this._suppressHeaderObserver = false;
    this.dbName = StoreLocatorComponent.DB_NAME;
    this.dbVersion = StoreLocatorComponent.DB_VERSION;
    this.db = null;
    this.selectedStore = null;
    this.lastSearchLocation = null;
    this.storeDataMap = new Map();
    this.initialUserInput = null;
    this.userHasEnteredValue = false;
    this.isUserTyping = false;
    this.showAllStores = false; // Flag to track if we should show all stores or just geocoded location
    this.cachedSearchResults = null; // Store search results for reuse
    this.selectedPlaceLatLng = null; // Store selected place lat/lng from autocomplete
    this.selectedPlaceBackup = null;
    this.pendingSelectedPlace = null;
    this.shouldRestoreSelectedPlace = false;

    // Selectors object - centralized selector definitions
    this.selectors = {
      // Header store elements
      headerNoStore: '.header-store .current-store--no-store',
      headerHasStore: '.header-store .current-store--has-store',
      headerStatusContainer: '.header-store .current-store-status',
      headerStoreName: '.header-store .store-name',
      headerStoreStatus: '.header-store .store-status',

      // Modals
      setStoreModal: '#set-store-modal',
      storeDetailsModal: '#store-details-modal',

      // Set store modal elements
      noStoreWarning: '.set-store__no-store-warning',
      setStoreInput: '.set-store__input',
      setStoreLabel: '.set-store__label',
      setStoreForm: '.set-store__form',
      setStoreModalContent: '.set-store-modal__content',
      closestStore: '.set-store__closest-store',
      storeList: '.set-store__store-list',
      findButton: '.set-store__find-button',
      setButton: '.set-store__set-button',
      selectButton: '.set-store__select-button',
      locationInput: '#store-location-input',
      locationIcon: '.set-store__input-icon',
      storeSuggestions: '#store-suggestions',

      // Closest store elements
      closestStoreName: '.closest-store__name',
      closestStoreDistance: '.closest-store__distance',
      closestStoreAddress: '.closest-store__address',
      closestStoreCheckIcon: '.closest-store__check-icon',
      closestStoreChangeLink: '.closest-store__change-link',

      // Store list elements
      storeItem: '.set-store__store-item',
      storeCheckIcon: '.set-store__store-check-icon',
      storeName: '.set-store__store-name',
      storeDistance: '.set-store__store-distance',
      storeAddress: '.set-store__store-address',
      storeHeader: '.set-store__store-header',
      storeInfo: '.set-store__store-info',
      storeRadio: 'input[name="store"]',
      storeRadioChecked: 'input[name="store"]:checked',
      storeRadioInput: 'input[type="radio"]',

      // Store details modal elements
      storeDetailsName: '.store-details__name',
      storeDetailsAddress: '.store-details__address',
      storeDetailsPhoneText: '.store-details__phone-text',
      storeDetailsPhone: '.store-details__phone',
      storeDetailsEmailText: '.store-details__email-text',
      storeDetailsEmail: '.store-details__email',
      storeDetailsHours: '.store-details__hours',
      storeDetailsSpecialHours: '.store-details__special-hours',
      storeDetailsHolidays: '.store-details__holidays',
      viewAllButton: '.store-details__view-all-button',
      changeStoreButton: '.store-details__change-store-button',
      storeDetailsActions: '.store-details-modal__actions',
      setStoreModalActions: '#set-store-modal .store-details-modal__actions',

      // Other elements
      dialogComponent: 'dialog-component',
      suggestionItem: '.suggestion-item',
      svgWrapper: '.svg-wrapper'
    };

    // Initialize IndexedDB
    this.initializeDatabase().then(async () => {
      this.selectedStore = await this.loadSelectedStore();
    }).catch(() => {
      // IndexedDB initialization failed, store will be loaded later
      this.selectedStore = null;
    });
    const modal = this.getModal('set-store-modal');
    if (modal instanceof HTMLElement) {
      const dialog = modal.querySelector('.set-store-dialog');
      dialog.addEventListener('dialog:close', () => {
        this.restoreSelectedPlaceBackup();
        document.body.classList.remove('store-header-modal--open');
      })
    }

  }

  /**
   * Initialize IndexedDB database
   */
  async initializeDatabase() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        console.warn('IndexedDB not supported, using localStorage');
        resolve(null);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('IndexedDB initialization failed:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains('selectedStore')) {
          const store = db.createObjectStore('selectedStore', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('storeResults')) {
          const store = db.createObjectStore('storeResults', { keyPath: 'location' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Get database instance
   */
  async getDatabase() {
    if (this.db) {
      if (this.db.objectStoreNames.contains('selectedStore')) {
        return this.db;
      }
      this.db.close();
      this.db = null;
    }
    await this.initializeDatabase();
    return this.db;
  }

  /**
   * Helper: Get modal element
   */
  getModal(modalId) {
    return this.querySelector(`#${modalId}`) || document.getElementById(modalId);
  }

  backupAndClearSelectedPlace() {
    if (!this.shouldRestoreSelectedPlace) {
      this.selectedPlaceBackup = window.localStorage.getItem('selectedPlace');
      this.shouldRestoreSelectedPlace = true;
    }
    this.pendingSelectedPlace = null;
    window.localStorage.removeItem('selectedPlace');
  }

  restoreSelectedPlaceBackup() {
    if (!this.shouldRestoreSelectedPlace) return;

    const currentSelectedPlace = window.localStorage.getItem('selectedPlace');
    if (!currentSelectedPlace && this.selectedPlaceBackup) {
      window.localStorage.setItem('selectedPlace', this.selectedPlaceBackup);
    }

    this.selectedPlaceBackup = null;
    this.pendingSelectedPlace = null;
    this.shouldRestoreSelectedPlace = false;
  }

  commitSelectedPlaceChange() {
    if (this.pendingSelectedPlace) {
      window.localStorage.setItem('selectedPlace', this.pendingSelectedPlace);
    } else if (this.shouldRestoreSelectedPlace) {
      window.localStorage.removeItem('selectedPlace');
    }

    this.selectedPlaceBackup = null;
    this.pendingSelectedPlace = null;
    this.shouldRestoreSelectedPlace = false;
  }

  /**
   * Helper: Hide no-store warning
   */
  hideNoStoreWarning() {
    const modal = this.getModal('set-store-modal');
    if (modal) {
      const warning = modal.querySelector(this.selectors.noStoreWarning);
      if (warning instanceof HTMLElement) {
        warning.style.display = 'none';
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
    this.setupHeaderMutationObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  async initialize() {
    if (!this.selectedStore) {
      this.selectedStore = await this.loadSelectedStore();
      if (this.selectedStore) {
        this.#dispatchStoreChangedEvent(this.selectedStore);
      }
    }

    this.updateHeaderDisplay();
    this.setupHeaderClickHandlers();
    this.setupModalEventHandlers();
    this.setupGoogleMapsAutocomplete();
    this.checkAndRevalidateOnPageLoad();
  }

  /**
   * Check and revalidate store results on page load if cache TTL has expired
   */
  async checkAndRevalidateOnPageLoad() {
    try {
      const lastSearch = await this.getLastSearchLocation();
      const recent = await this.getMostRecentCachedResults();
      const addr = this.selectedStore?.address;
      const location = lastSearch || this.lastSearchLocation || recent?.location || (typeof addr === 'string' ? addr : (addr?.suburb || addr?.postcode || addr?.state || this.selectedStore?.name)) || null;
      if (location) {
        await this.findStoresByLocation(location);
      }
    } catch (e) {
      console.warn('[StoreLocator] Page load revalidation warning:', e);
    }
  }

  /**
   * Setup click handlers for header store elements
   */
  setupHeaderClickHandlers() {
    const noStoreElement = this.querySelector(this.selectors.headerNoStore);
    const hasStoreElement = this.querySelector(this.selectors.headerHasStore);

    const handleNoStoreClick = (e) => {
      const target = e.target;
      if (target instanceof HTMLElement && target.closest('.store-location-link')) {
        e.preventDefault();
      }
      this.openSetStoreModal();
      this.hideNoStoreWarning();

    };

    if (noStoreElement) {
      noStoreElement.addEventListener('click', handleNoStoreClick);
    } else {
      const fallback = document.querySelector(this.selectors.headerNoStore);
      if (fallback) fallback.addEventListener('click', handleNoStoreClick);
    }

    if (hasStoreElement) {
      hasStoreElement.addEventListener('click', () => this.openStoreDetailsModal());
    } else {
      const fallback = document.querySelector(this.selectors.headerHasStore);
      if (fallback) fallback.addEventListener('click', () => this.openStoreDetailsModal());
    }
  }

  /**
   * Setup event handlers for modal buttons
   */
  setupModalEventHandlers() {
    const viewAllButton = this.querySelector(this.selectors.viewAllButton) ||
      document.querySelector(this.selectors.viewAllButton);
    const changeStoreButton = this.querySelector(this.selectors.changeStoreButton) ||
      document.querySelector(this.selectors.changeStoreButton);

    if (viewAllButton) {
      viewAllButton.addEventListener('click', () => {
        // There is no action for now.
      });
    }

    if (changeStoreButton) {
      changeStoreButton.addEventListener('click', async () => {
        // Close store details modal without animation (skip closing animation)
        this.closeStoreDetailsModal(true);
        this.hideNoStoreWarning();

        // Set flag to show all stores
        this.showAllStores = true;

        // Open set store modal without animation (since we're transitioning from another modal)
        this.openSetStoreModal(true);

        // Wait a bit for modal to open, then display store list
        setTimeout(async () => {
          const modal = this.getModal('set-store-modal');
          if (modal instanceof HTMLElement) {
            const modalContent = modal.querySelector(this.selectors.setStoreModalContent);
            if (modalContent instanceof HTMLElement) {
              modalContent.classList.add('show-all-stores-view');
            }
          }

          // Display store list if we have cached results
          if (this.cachedSearchResults?.stores?.length || this.cachedSearchResults?.geocodedLocation) {
            let stores = this.cachedSearchResults.stores || [];

            // Add geocoded location to the beginning of stores list
            if (this.cachedSearchResults.geocodedLocation) {
              const geocodedStore = this.convertGeocodedLocationToStore(this.cachedSearchResults.geocodedLocation);
              stores = [geocodedStore, ...stores];
            }

            const currentStore = await this.loadSelectedStore();
            this.displayStoreList(stores, currentStore);
          }
          this.storeListClasses();
        }, 100);
      });
    }

    const findButton = this.querySelector(this.selectors.findButton) ||
      document.querySelector(this.selectors.findButton);
    const setButton = this.querySelector(this.selectors.setButton) ||
      document.querySelector(this.selectors.setButton);
    const selectButton = this.querySelector(this.selectors.selectButton) ||
      document.querySelector(this.selectors.selectButton);
    const changeLink = this.querySelector(this.selectors.closestStoreChangeLink) ||
      document.querySelector(this.selectors.closestStoreChangeLink);
    const locationInput = this.querySelector(this.selectors.locationInput) ||
      document.querySelector(this.selectors.locationInput);
    const locationIcon = this.querySelector(this.selectors.locationIcon) ||
      document.querySelector(this.selectors.locationIcon);

    if (findButton) {
      findButton.addEventListener('click', () => this.searchStores(this.selectedPlaceLatLng));
    }

    if (setButton) {
      setButton.addEventListener('click', () => this.saveSelectedStore());
    }

    if (selectButton) {
      selectButton.addEventListener('click', () => this.saveSelectedStore());
    }

    if (changeLink) {
      changeLink.addEventListener('click', async () => {
        this.showAllStores = true;
        this.hideNoStoreWarning();

        // Add class to modal content for styling
        const modal = this.getModal('set-store-modal');
        if (modal instanceof HTMLElement) {
          const modalContent = modal.querySelector(this.selectors.setStoreModalContent);
          if (modalContent instanceof HTMLElement) {
            modalContent.classList.add('show-all-stores-view');
          }
        }

        if (this.cachedSearchResults?.stores?.length) {
          const stores = this.cachedSearchResults.stores || [];
          const currentStore = await this.loadSelectedStore();
          this.displayStoreList(stores, currentStore);
        }
      });
    }

    if (locationInput) {
      // Track user input - update whenever user types
      locationInput.addEventListener('input', (e) => {
        if (e.target instanceof HTMLInputElement) {
          // Always update the initial user input to current value
          this.initialUserInput = e.target.value;
          this.userHasEnteredValue = true;
          this.isUserTyping = true;
          this.selectedPlaceLatLng = null; // Clear coordinates on new input
          this.backupAndClearSelectedPlace();
        }
      });

      // Track when user starts typing
      locationInput.addEventListener('focus', () => {
        this.isUserTyping = true;
      });

      // Track when user stops typing (with a small delay)
      locationInput.addEventListener('blur', () => {
        setTimeout(() => {
          this.isUserTyping = false;
        }, 100);
      });

      locationInput.addEventListener('keypress', (e) => {
        if (e instanceof KeyboardEvent && e.key === 'Enter') {
          e.preventDefault();
          this.searchStores(this.selectedPlaceLatLng);
        }
      });
    }

    if (locationIcon) {
      locationIcon.addEventListener('click', () => {
        const input = this.querySelector(this.selectors.locationInput) ||
          document.querySelector(this.selectors.locationInput);
        if (input instanceof HTMLInputElement) {
          this.getCurrentLocation(input);
        }
      });
    }
    this.storeListClasses();
  }

  async waitForGoogleMaps() {
    if (typeof window.google !== 'undefined' && window.google.maps && window.google.maps.places) {
      return true;
    }

    const maxWait = 5000;
    const startTime = Date.now();
    while (Date.now() - startTime < maxWait) {
      if (typeof window.google !== 'undefined' && window.google.maps && window.google.maps.places) {
        return true;
      }
      await new Promise(resolve => requestAnimationFrame(resolve));
    }
    return false;
  }

  /**
   * Setup Google Maps Autocomplete for store location input
   */
  async setupGoogleMapsAutocomplete() {
    // Wait for Google Maps API to be available
    const isReady = await this.waitForGoogleMaps();
    if (!isReady) {
      console.warn('Google Maps API not loaded for store locator autocomplete');
      return;
    }

    const input = this.querySelector(this.selectors.locationInput) || document.querySelector(this.selectors.locationInput);
    const suggestions = this.querySelector(this.selectors.storeSuggestions) || document.querySelector(this.selectors.storeSuggestions);

    if (!input) {
      console.error('Cannot find store-location-input element');
      return;
    }

    // Initialize Google Places Autocomplete
    const autocompleteOptions = {
      types: [],
      componentRestrictions: { country: 'au' },
      fields: ['place_id', 'name', 'geometry', 'formatted_address', 'address_components']
    };

    const autocomplete = new google.maps.places.Autocomplete(input, autocompleteOptions);

    // Handle place selection
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.geometry) {
        // Set input value to selected place
        if (place.formatted_address) {
          input.value = place.formatted_address;
        } else if (place.name) {
          input.value = place.name;
        }
        // Store selected coordinates from standard autocomplete
        if (place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          this.selectedPlaceLatLng = { lat, lng };
        }
        this.pendingSelectedPlace = JSON.stringify(place);
        // Clear suggestions
        if (suggestions) {
          suggestions.innerHTML = '';
          suggestions.classList.remove('has-suggestions');
        }
      }
    });

    // Debounce function for suggestions
    const debounce = (func, delay) => {
      let timeoutId;
      return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    };

    // Fetch suggestions with debounce
    const fetchSuggestions = debounce((inputValue) => {
      if (!suggestions) return;

      const service = new google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: inputValue,
          ...autocompleteOptions
        },
        (predictions, status) => {
          suggestions.innerHTML = '';
          suggestions.classList.remove('has-suggestions');
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            predictions.forEach((prediction) => {
              const div = document.createElement('div');
              div.textContent = prediction.description;
              div.className = this.selectors.suggestionItem.substring(1);
              div.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Set input value and ensure it persists
                const selectedValue = prediction.description;
                input.value = selectedValue;

                // Store the selected value to prevent autocomplete from overriding
                this.userHasEnteredValue = true;
                this.initialUserInput = selectedValue;

                // Clear suggestions
                suggestions.innerHTML = '';
                suggestions.classList.remove('has-suggestions');

                // Fetch place details to get lat/lng (predictions do not include geometry)
                if (prediction.place_id) {
                  const placesService = new google.maps.places.PlacesService(document.createElement('div'));
                  placesService.getDetails(
                    {
                      placeId: prediction.place_id,
                      fields: ['geometry', 'name', 'formatted_address', 'address_components']
                    },
                    (place, detailsStatus) => {
                      if (
                        detailsStatus === google.maps.places.PlacesServiceStatus.OK &&
                        place?.geometry?.location
                      ) {
                        const lat = place.geometry.location.lat();
                        const lng = place.geometry.location.lng();
                        // Store the lat/lng instead of calling API immediately
                        this.selectedPlaceLatLng = { lat, lng };
                        this.pendingSelectedPlace = JSON.stringify(place);
                      } else {
                        console.warn('Place details failed', detailsStatus, place);
                      }
                    }
                  );
                }
                input.blur();
                if (input.value !== selectedValue) {
                  input.value = selectedValue;
                }
              };
              suggestions.appendChild(div);
            });
            // Add class when suggestions are populated
            if (predictions.length > 0) {
              suggestions.classList.add('has-suggestions');
            }
          }
        }
      );
    }, 300);

    // Add input listener for suggestions
    if (input && suggestions) {
      input.addEventListener('input', function () {
        if (input.value.trim()) {
          fetchSuggestions(input.value);
        } else {
          suggestions.innerHTML = '';
          suggestions.classList.remove('has-suggestions');
        }
      });
    }

    // Hide suggestions when clicking outside
    document.addEventListener('click', (e) => {
      if (suggestions && !input.contains(e.target) && !suggestions.contains(e.target)) {
        if (suggestions.classList.contains('has-suggestions')) {
          const firstSuggestion = suggestions.querySelector(this.selectors.suggestionItem);
          if (firstSuggestion) {
            firstSuggestion.click();
            return;
          }
        }
        suggestions.innerHTML = '';
        suggestions.classList.remove('has-suggestions');
      }
    });
  }

  /**
   * Load selected store from IndexedDB (with 30 days expiration check)
   */
  async loadSelectedStore() {
    try {
      const db = await this.getDatabase();
      if (!db) {
        return null;
      }

      if (!db.objectStoreNames.contains('selectedStore')) {
        return null;
      }

      return new Promise((resolve) => {
        try {
          const transaction = db.transaction(['selectedStore'], 'readonly');

          transaction.onerror = () => {
            resolve(null);
          };

          const store = transaction.objectStore('selectedStore');
          const request = store.get(StoreLocatorComponent.PREFERRED_STORE_KEY);

          request.onsuccess = () => {
            const result = request.result;
            if (result?.data) {
              // Check if expired (30 days)
              if (result.expiresAt && Date.now() > result.expiresAt) {
                // Expired, delete and return null
                this.deleteSelectedStore();
                resolve(null);
              } else {
                const formatted = this.normalizeStoreData(result.data);
                resolve(formatted);
              }
            } else {
              resolve(null);
            }
          };

          request.onerror = () => {
            console.error('Error reading from IndexedDB:', request.error);
            resolve(null);
          };
        } catch (error) {
          resolve(null);
        }
      });
    } catch (e) {
      console.error('Error loading selected store:', e);
      return null;
    }
  }

  /**
   * Normalize store data to match internal structure
   */
  normalizeStoreData(storeData) {
    if (!storeData) return null;

    // Already in correct format
    if (storeData.name && typeof storeData.address === 'string') {
      return storeData.name.trim() !== '' ? storeData : null;
    }

    // API format - needs conversion
    if (storeData._apiData || (storeData.address && typeof storeData.address === 'object')) {
      const formatted = this.convertApiStoreToInternalFormat(storeData);
      return formatted?.name?.trim() ? formatted : null;
    }

    // Fallback - has name
    if (storeData.name && typeof storeData.name === 'string' && storeData.name.trim() !== '') {
      return storeData;
    }

    return null;
  }

  /**
   * Save selected store to IndexedDB (with 30 days expiration)
   */
  async saveSelectedStoreToDatabase(store) {
    if (!store || (typeof store === 'object' && Object.keys(store).length === 0)) {
      return;
    }
    try {
      const db = await this.getDatabase();
      const hash = await this.computeHash(store);
      if (db) {
        return new Promise((resolve) => {
          try {
            const transaction = db.transaction(['selectedStore'], 'readwrite');
            const objectStore = transaction.objectStore('selectedStore');

            const expiresAt = Date.now() + (30 * 24 * 60 * 60 * 1000); // 30 days

            const data = {
              id: StoreLocatorComponent.PREFERRED_STORE_KEY,
              data: store,
              hash: hash,
              timestamp: Date.now(),
              expiresAt: expiresAt
            };

            const request = objectStore.put(data);

            request.onsuccess = () => {
              this.selectedStore = store;
              // Only dispatch events here; header UI refresh is handled by caller (e.g. saveSelectedStore)
              this.#dispatchStoreChangedEvent(store);
              window.dispatchEvent(new CustomEvent('preferred-store-updated'));
              resolve();
            };

            request.onerror = () => {
              console.error('Error saving to IndexedDB:', request.error);
              this.selectedStore = store;
              // Dispatch event even on error (store is still saved in memory)
              this.#dispatchStoreChangedEvent(store);
              window.dispatchEvent(new CustomEvent('preferred-store-updated'));
              resolve();
            };

            transaction.onerror = () => {
              console.error('Transaction error:', transaction.error);
            };
          } catch (transactionError) {
            console.warn('[StoreLocator]', this.debugId, 'transactionError in saveSelectedStoreToDatabase, falling back to in-memory update', transactionError);
            this.selectedStore = store;
            this.#dispatchStoreChangedEvent(store);
            window.dispatchEvent(new CustomEvent('preferred-store-updated'));
            resolve();
          }
        });
      } else {
        this.selectedStore = store;
        this.#dispatchStoreChangedEvent(store);
        window.dispatchEvent(new CustomEvent('preferred-store-updated'));
      }
    } catch (e) {
      console.error('Error saving store:', e);
      this.selectedStore = store;
      this.#dispatchStoreChangedEvent(store);
    }
  }

  /**
   * Dispatch store changed event for location-based pricing
   * @private
   */
  #dispatchStoreChangedEvent(store) {
    if (!store) return;

    // Resolve pricebook from multiple possible sources (top-level, _apiData, metafields)
    let pricebook = null;

    const fromPriceBook = (v) => {
      if (v == null || v === '') return null;
      return typeof v === 'string' ? v : (v?.value ?? null) || null;
    };

    pricebook = fromPriceBook(store.priceBook);
    if (!pricebook && store._apiData) {
      pricebook = fromPriceBook(store._apiData.priceBook);
      if (!pricebook && store._apiData._apiData?.priceBook) {
        pricebook = fromPriceBook(store._apiData._apiData.priceBook);
      }
    }
    if (!pricebook) {
      pricebook = store.metafields?.custom?.price_book?.value ?? fromPriceBook(store.metafields?.custom?.price_book) ?? null;
    }

    // Dispatch location changed event
    document.dispatchEvent(new CustomEvent('location:changed', {
      detail: {
        store: store,
        pricebook: pricebook,
        storeName: store.name || null
      },
      bubbles: true
    }));

    // Also dispatch pricebook changed event if pricebook exists
    if (pricebook) {
      document.dispatchEvent(new CustomEvent('pricebook:changed', {
        detail: {
          pricebook: pricebook
        },
        bubbles: true
      }));
      window.dispatchEvent(new CustomEvent('preferred-store-updated'));
    }
  }

  /**
   * Update header display with current store info
   */
  async updateHeaderDisplay() {
    let store = this.selectedStore;
    if (!store) {
      store = await this.loadSelectedStore();
    }
    const noStoreEl = this.querySelector(this.selectors.headerNoStore);
    const hasStoreEl = this.querySelector(this.selectors.headerHasStore);
    const statusContainer = this.querySelector(this.selectors.headerStatusContainer);
    const nameEl = this.querySelector(this.selectors.headerStoreName);
    const statusEl = this.querySelector(this.selectors.headerStoreStatus);
    const statusElParent = statusEl?.parentElement;

    if (!store || !store.address) {
      this._suppressHeaderObserver = true;
      this.hideStoreElements(noStoreEl, hasStoreEl, statusContainer);
      if (nameEl) nameEl.textContent = '';
      if (statusEl) statusEl.innerHTML = '';
      setTimeout(() => {
        this._suppressHeaderObserver = false;
      }, 0);
      return;
    }

    this.selectedStore = store;

    const hasValidStore = store?.name && typeof store.name === 'string' && store.name.trim() !== '';
    const applyHeaderState = () => {
      this._suppressHeaderObserver = true;
      if (hasValidStore) {
        this.showStoreElements(noStoreEl, hasStoreEl, statusContainer);
        if (nameEl) nameEl.textContent = store.name;
        if (statusEl) {
          const status = this.getStoreStatus(store);
          const isOpen = status.open;

          if (status && status.text) {
            statusEl.innerHTML = `<b>${status.text}</b>${status.until ? ' until ' + status.until : ''}`;

            const svgIcon = statusElParent?.querySelector('svg');
            if (svgIcon) {
              const checkedSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z" fill="#34C759"/>
              <mask id="mask0_10993_25419" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
              <rect width="16" height="16" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_10993_25419)">
              <path d="M6.36752 11.5386L3.05469 8.22573L3.53035 7.75007L6.36752 10.5872L12.4714 4.4834L12.947 4.95906L6.36752 11.5386Z" fill="white"/>
              </g>
              </svg>`;

              const closedSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z" fill="#830000"/>
              <path d="M4.26669 12.2052L3.79486 11.7333L7.52819 8L3.79486 4.26666L4.26669 3.79483L8.00003 7.52816L11.7334 3.79483L12.2052 4.26666L8.47186 8L12.2052 11.7333L11.7334 12.2052L8.00003 8.47183L4.26669 12.2052Z" fill="white"/>
              </svg>`;

              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = isOpen ? checkedSvg : closedSvg;
              const newSvg = tempDiv.firstElementChild;
              if (newSvg) {
                newSvg.setAttribute('class', svgIcon.getAttribute('class') || '');
                svgIcon.replaceWith(newSvg);
              }
            }

            statusElParent?.classList.remove('hidden');
          } else {
            statusElParent?.classList.add('hidden');
          }
        }
      } else {
        this.hideStoreElements(noStoreEl, hasStoreEl, statusContainer);
        if (nameEl) nameEl.textContent = '';
        if (statusEl) statusEl.innerHTML = '';
      }
      setTimeout(() => {
        this._suppressHeaderObserver = false;
      }, 0);
    };

    applyHeaderState();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        const noStoreEl2 = this.querySelector(this.selectors.headerNoStore);
        const hasStoreEl2 = this.querySelector(this.selectors.headerHasStore);
        const statusContainer2 = this.querySelector(this.selectors.headerStatusContainer);
        this._suppressHeaderObserver = true;
        this.showStoreElements(noStoreEl2 || noStoreEl, hasStoreEl2 || hasStoreEl, statusContainer2 || statusContainer);
        setTimeout(() => {
          this._suppressHeaderObserver = false;
        }, 0);
      });
    }
  }

  /**
  * Format time from 24h to 12h format
  * @param {string|undefined} t
  */
  fmtTime(t) {
    return sharedFmtTime(t);
  }

  /**
   * Single source of truth for open/closed status (open + text + until).
   * Delegates to the shared store-status module so the header, the list cards
   * and the detail view all compute status identically.
   */
  getStoreStatus(store, timeZone) {
    return sharedGetStoreStatus(store, timeZone);
  }

  setupHeaderMutationObserver() {
    if (this._headerObserver) return;
    const headerRoot = this.querySelector('.header-store');
    if (!headerRoot) return;

    this._headerObserver = new MutationObserver((mutations) => {
      if (this._suppressHeaderObserver) {
        return;
      }
      this.updateHeaderDisplay();
    });

    this._headerObserver.observe(headerRoot, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  /**
   * Show store elements, hide no-store message
   */
  showStoreElements(noStoreEl, hasStoreEl, statusContainer) {
    if (noStoreEl instanceof HTMLElement) {
      noStoreEl.classList.add('store-state-hidden');
    }
    if (hasStoreEl instanceof HTMLElement) {
      hasStoreEl.classList.remove('store-state-hidden');
    }
    if (statusContainer instanceof HTMLElement) {
      statusContainer.classList.remove('store-state-hidden');
    }
  }

  /**
   * Hide store elements, show no-store message
   */
  hideStoreElements(noStoreEl, hasStoreEl, statusContainer) {
    if (noStoreEl instanceof HTMLElement) {
      noStoreEl.classList.remove('store-state-hidden');
    }
    if (hasStoreEl instanceof HTMLElement) {
      hasStoreEl.classList.add('store-state-hidden');
    }
    if (statusContainer instanceof HTMLElement) {
      statusContainer.classList.add('store-state-hidden');
    }
  }

  /**
   * Calculate store status (open/closed)
   */
  calculateStoreStatus(store, timeZone) {
    return sharedCalculateStoreStatus(store, timeZone);
  }

  getNextOpeningTime(rawHoursArray, currentDayInStore, tz) {
    return sharedGetNextOpeningTime(rawHoursArray, currentDayInStore, tz);
  }

  /**
   * Find next opening time
   */
  findNextOpening(openingHours, currentDayIndex) {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Check next 7 days
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      const nextDayName = dayNames[nextDayIndex];
      const hours = openingHours[nextDayName];

      if (hours && hours !== 'Closed') {
        const hoursMatch = hours.match(/^(.+?)\s*-\s*(.+)$/);
        if (hoursMatch) {
          const openTime = hoursMatch[1];
          if (i === 1) {
            return `tomorrow at ${openTime}`;
          } else {
            return `${dayLabels[nextDayIndex]} at ${openTime}`;
          }
        }
      }
    }

    return null;
  }

  /**
   * Parse time string (e.g., "8:30 am")
   */
  parseTimeString(timeStr) {
    const match = timeStr.match(/(\d+):(\d+)\s*(am|pm)/i);
    return match ? [parseInt(match[1]), parseInt(match[2]), match[3].toLowerCase()] : [0, 0, 'am'];
  }

  /**
   * Convert time to minutes since midnight
   */
  convertToMinutes(hour, min, period) {
    if (period === 'pm' && hour !== 12) hour += 12;
    if (period === 'am' && hour === 12) hour = 0;
    return hour * 60 + min;
  }

  /**
   * Open Store Details Modal
   */
  openStoreDetailsModal() {
    const modal = this.getModal('store-details-modal');
    if (modal instanceof HTMLElement) {
      const dialog = modal.querySelector(this.selectors.dialogComponent);
      if (dialog?.showDialog && typeof dialog.showDialog === 'function') {
        dialog.showDialog();
        this.renderStoreDetails();
      }
    }
  }

  /**
   * Close Store Details Modal
   */
  closeStoreDetailsModal(skipAnimation = false) {
    const modal = this.getModal('store-details-modal');
    if (modal) {
      const dialogElement = modal.querySelector('dialog');
      this.hideNoStoreWarning();

      // Add no-animation class to skip closing animation
      if (skipAnimation && dialogElement instanceof HTMLElement) {
        dialogElement.classList.add('no-animation');
      }

      const dialog = modal.querySelector(this.selectors.dialogComponent);
      if (dialog?.closeDialog && typeof dialog.closeDialog === 'function') {
        dialog.closeDialog();
      }

      // Remove no-animation class after close for next time
      if (skipAnimation && dialogElement instanceof HTMLElement) {
        setTimeout(() => {
          dialogElement.classList.remove('no-animation');
        }, 50);
      }
    }
  }

  /**
   * Open Set Store Modal and load cached data
   */
  async openSetStoreModal(skipAnimation = false) {
    this.userHasEnteredValue = false;
    this.initialUserInput = null;
    this.isUserTyping = false;
    this.showAllStores = false;
    this.cachedSearchResults = null;
    this.selectedPlaceLatLng = null; // Clear selected coordinates

    const modal = this.getModal('set-store-modal');
    this.hideNoStoreWarning();
    document.body.classList.add('store-header-modal--open')

    if (modal instanceof HTMLElement) {
      // Remove show-all-stores-view class when opening modal
      const modalContent = modal.querySelector(this.selectors.setStoreModalContent);
      if (modalContent instanceof HTMLElement) {
        modalContent.classList.remove('show-all-stores-view');
      }
      const action = modal.querySelector(this.selectors.storeDetailsActions);
      if (action instanceof HTMLElement) {
        action.style.display = 'flex';
      }

      const dialogElement = modal.querySelector('dialog');
      // Add or remove no-animation class based on skipAnimation parameter
      if (dialogElement instanceof HTMLElement) {
        if (skipAnimation) {
          dialogElement.classList.add('no-animation');
        } else {
          dialogElement.classList.remove('no-animation');
        }
      }

      const dialog = modal.querySelector(this.selectors.dialogComponent);
      if (dialog?.showDialog && typeof dialog.showDialog === 'function') {
        dialog.showDialog();
        await this.loadCachedDataIntoForm();
      }
    }
  }

  /**
   * Render Store Details content
   */
  renderStoreDetails() {
    if (!this.selectedStore) return;

    const modal = this.getModal('store-details-modal');
    if (!modal) return;

    const nameEl = modal.querySelector(this.selectors.storeDetailsName);
    const addressEl = modal.querySelector(this.selectors.storeDetailsAddress);
    const phoneEl = modal.querySelector(this.selectors.storeDetailsPhoneText);
    const emailEl = modal.querySelector(this.selectors.storeDetailsEmailText);
    const hoursEl = modal.querySelector(this.selectors.storeDetailsHours);
    const specialHoursEl = modal.querySelector(this.selectors.storeDetailsSpecialHours);
    const holidaysEl = modal.querySelector(this.selectors.storeDetailsHolidays);

    if (nameEl) nameEl.textContent = this.selectedStore.name;

    const displayAddress = this.selectedStore.addressForDetails || this.selectedStore.address;
    if (addressEl) addressEl.textContent = displayAddress;

    if (phoneEl) {
      const container = phoneEl.closest(this.selectors.storeDetailsPhone);
      if (this.selectedStore.phone && container instanceof HTMLElement) {
        phoneEl.textContent = this.selectedStore.phone;
        container.style.display = 'flex';
      } else if (container instanceof HTMLElement) {
        container.style.display = 'none';
      }
    }

    if (emailEl) {
      const container = emailEl.closest(this.selectors.storeDetailsEmail);
      const rawEmail = (this.selectedStore?._apiData?.contactEmail?.value || '').trim();
      const normalizedEmail = rawEmail.replace(/^mailto:/i, '');
      if (normalizedEmail && container instanceof HTMLElement) {
        emailEl.textContent = normalizedEmail;
        if (emailEl instanceof HTMLAnchorElement) {
          emailEl.href = `mailto:${normalizedEmail}`;
        }
        container.style.display = 'flex';
      } else if (container instanceof HTMLElement) {
        container.style.display = 'none';
      }
    }

    const storeTimezone = this.getStoreTimezone(this.selectedStore);

    const isOpen = this.isStoreOpen(this.selectedStore, storeTimezone);

    if (hoursEl) {
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

      const currentDayInStore = this.getStoreCurrentDayOfWeek(storeTimezone);

      let parsedHours = null;
      let rawHoursArray = null;

      const openingHoursSource =
        this.selectedStore?.openingHours?.value ||
        this.selectedStore?._apiData?.openingHours?.value ||
        this.selectedStore?._apiData?._apiData?.openingHours?.value ||
        this.selectedStore?.openingHours ||
        this.selectedStore?._apiData?.openingHours;

      if (openingHoursSource) {
        const rawVal = typeof openingHoursSource === 'string' ? openingHoursSource : (openingHoursSource.value || '');
        if (rawVal) {
          try {
            rawHoursArray = JSON.parse(rawVal);
          } catch (e) { }
        } else if (Array.isArray(openingHoursSource)) {
          rawHoursArray = openingHoursSource;
        } else if (openingHoursSource.value && Array.isArray(openingHoursSource.value)) {
          rawHoursArray = openingHoursSource.value;
        }

        if (typeof openingHoursSource === 'string') {
          parsedHours = this.parseOpeningHoursJSON(openingHoursSource);
        } else if (typeof openingHoursSource === 'object' && !openingHoursSource.value) {
          parsedHours = openingHoursSource;
        }
      }

      hoursEl.innerHTML = days.map((day, index) => {
        let hoursText = 'Closed';

        const apiDayOfWeek = (index + 1) % 7;

        if (Array.isArray(rawHoursArray)) {
          const d = rawHoursArray.find(h => h.day_of_week === apiDayOfWeek || h.day_of_week === (index + 1));
          if (d && d.start_time && d.end_time) {
            hoursText = this.fmtTime(d.start_time) + ' – ' + this.fmtTime(d.end_time);
          }
        } else {
          hoursText = parsedHours?.[day] || 'Closed';
        }

        const isToday = apiDayOfWeek === currentDayInStore;
        const rowClass = isToday ? 'store-details__hours-row is-today' : 'store-details__hours-row';

        return `<div class="${rowClass}">
          <span>${dayNames[index]}</span>
          <span>${hoursText}</span>
        </div>`;
      }).join('');
    }

    this.renderSpecialHours(specialHoursEl);

    if (holidaysEl) {
      holidaysEl.innerHTML = `
        <span class="store-details__holidays-icon">${this.getInfoIcon()}</span>
        <span class="store-details__holidays-text">Trading hours may vary on public holidays. Please contact us directly for more details.</span>
      `;
    }
  }

  renderSpecialHours(specialHoursEl) {
    if (!specialHoursEl) return;

    let specialRawArray = null;

    const openingHoursSource =
        this.selectedStore?.openingHours?.value ||
        this.selectedStore?._apiData?.openingHours?.value ||
        this.selectedStore?._apiData?._apiData?.openingHours?.value ||
        this.selectedStore?.openingHours ||
        this.selectedStore?._apiData?.openingHours;

    if (openingHoursSource) {
        const rawVal = typeof openingHoursSource === 'string' ? openingHoursSource : (openingHoursSource.value || '');
        if (rawVal) {
            try {
                specialRawArray = JSON.parse(rawVal);
            } catch (e) { }
        } else if (Array.isArray(openingHoursSource)) {
            specialRawArray = openingHoursSource;
        } else if (openingHoursSource.value && Array.isArray(openingHoursSource.value)) {
            specialRawArray = openingHoursSource.value;
        }
    }

    const now = new Date();
    const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');

    const specials = Array.isArray(specialRawArray)
        ? specialRawArray.filter(h => {
            if (!h || !h.date || String(h.date).trim() === '') return false;
            const datePart = String(h.date).split(' ')[0];
            return datePart >= todayStr;
        })
        : [];

    if (specials.length === 0) {
        specialHoursEl.innerHTML = '';
        specialHoursEl.style.display = 'none';
        return;
    }

    const weekdaysShort = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    specials.sort((a, b) => {
        const da = String(a.date).split(' ')[0];
        const db = String(b.date).split(' ')[0];
        return da.localeCompare(db);
    });

    specialHoursEl.innerHTML = specials.map(item => {
        const datePart = String(item.date).split(' ')[0];
        const [y, m, d] = datePart.split('-').map(Number);
        const dt = new Date(y, m - 1, d);

        const weekday = weekdaysShort[dt.getDay()];
        const dateStr = d + ' ' + monthsShort[m - 1];

        const hasHours = item.start_time && item.end_time;
        const hoursStr = hasHours
            ? this.fmtTime(item.start_time) + ' – ' + this.fmtTime(item.end_time)
            : 'Closed';

        return `<div class="store-details__hours-row">
          <span>${weekday}</span>
          <span>${dateStr}</span>
          <span>${hoursStr}</span>
        </div>`;
    }).join('');

    specialHoursEl.style.display = '';
  }

  getStoreTimezone(store) {
    return sharedGetStoreTimezone(store);
  }

  getStoreCurrentDayOfWeek(timeZone) {
    return sharedGetStoreCurrentDayOfWeek(timeZone);
  }

  isStoreOpen(store, timeZone) {
    return sharedIsStoreOpen(store, timeZone);
  }

  /**
   * Reset form to initial state
   */
  resetForm() {
    const modal = this.getModal('set-store-modal');
    if (!modal) return;

    const input = modal.querySelector(this.selectors.setStoreInput);
    const label = modal.querySelector(this.selectors.setStoreLabel);
    const form = modal.querySelector(this.selectors.setStoreForm);
    const closestSection = modal.querySelector(this.selectors.closestStore);
    const storeList = modal.querySelector(this.selectors.storeList);
    const findButton = modal.querySelector(this.selectors.findButton);
    const selectButton = modal.querySelector(this.selectors.selectButton);
    const setButton = modal.querySelector(this.selectors.setButton);
    const actions = modal.querySelector(this.selectors.storeDetailsActions);

    if (label instanceof HTMLElement) {
      label.style.display = 'block';
      label.style.visibility = 'visible';
      label.style.opacity = '1';
    }
    if (form instanceof HTMLElement) {
      form.style.display = 'block';
    }
    if (input instanceof HTMLInputElement) {
      // Restore initial user input if it exists, otherwise clear
      if (this.initialUserInput) {
        input.value = this.initialUserInput;
      } else {
        input.value = '';
      }
      input.disabled = false;
    }
    if (closestSection instanceof HTMLElement) closestSection.style.display = 'none';
    if (storeList instanceof HTMLElement) storeList.style.display = 'none';
    if (findButton instanceof HTMLButtonElement) {
      findButton.style.display = 'block';
      findButton.disabled = false;
      findButton.textContent = 'FIND MY STORE';
    }
    if (selectButton instanceof HTMLElement) selectButton.style.display = 'none';
    if (setButton instanceof HTMLElement) setButton.style.display = 'none';
    if (actions instanceof HTMLElement) actions.style.display = 'none';
  }

  /**
   * Load cached data from IndexedDB and populate form
   */
  async loadCachedDataIntoForm() {
    const modal = this.getModal('set-store-modal');
    if (!modal) return;

    // Skip loading cached data if we explicitly cleared cache (for fresh search)
    // This happens when opening modal from "Change Store" button
    const shouldSkipCache = this.cachedSearchResults === null && this.showAllStores === false;

    const input = modal.querySelector(this.selectors.setStoreInput);
    const closestSection = modal.querySelector(this.selectors.closestStore);
    const storeList = modal.querySelector(this.selectors.storeList);

    try {
      const currentStore = await this.loadSelectedStore();
      const persistedLastSearchLocation = await this.getLastSearchLocation();
      if (currentStore) {
        this.showAllStores = true;
      }
      const lastLocation = persistedLastSearchLocation ||
        this.lastSearchLocation ||
        currentStore?.address;

      // If we should skip cache, reset form and return
      // if (shouldSkipCache) {
      //   this.resetForm();
      //   return;
      // }

      if (lastLocation) {
        let cachedResults = await this.getCachedStoreResults(lastLocation);

        if (!cachedResults?.stores?.length) {
          cachedResults = await this.getMostRecentCachedResults();
          if (cachedResults?.location) {
            this.lastSearchLocation = cachedResults.location;
          }
        }

        if (cachedResults?.stores?.length || cachedResults?.geocodedLocation) {
          // Cache the results for potential reuse
          this.cachedSearchResults = cachedResults;

          // Only set input value if user hasn't manually entered anything and not currently typing
          if (input instanceof HTMLInputElement && !this.userHasEnteredValue && !this.isUserTyping) {
            const preferredInputValue = persistedLastSearchLocation || cachedResults.location || lastLocation;
            input.value = preferredInputValue;
          }
          // Don't override user input if they're actively typing

          let stores = cachedResults.stores || [];
          if (cachedResults.geocodedLocation) {
            const geocodedStore = this.convertGeocodedLocationToStore(cachedResults.geocodedLocation);
            if (!stores.some(s => s.id === geocodedStore.id)) {
              stores = [geocodedStore, ...stores];
            }
          }
          const seenIds = new Set();
          stores = stores.filter(store => {
            if (!store || !store.id) return false;
            const id = String(store.id);
            if (seenIds.has(id)) return false;
            seenIds.add(id);
            return true;
          });

          if (!this.showAllStores) {
            const storesToShow = stores.slice(0, 3);
            await this.displayClosestStore(storesToShow);
          } else if (stores.length > 0) {
            this.displayStoreList(stores, currentStore);
          }
          return;
        }
      }

      this.resetForm();
      // Only set input value if user hasn't manually entered anything and not currently typing
      if (input instanceof HTMLInputElement && !this.userHasEnteredValue && !this.isUserTyping) {
        const preferredInputValue = persistedLastSearchLocation || currentStore?.address || '';
        input.value = preferredInputValue;
      }
      // Don't override user input if they're actively typing
    } catch (error) {
      console.error('Error loading cached data:', error);
      this.resetForm();
    }
  }

  /**
   * Get current location using geolocation API
   */
  /**
   * Show warning message when no stores found
   */
  showNoStoreWarning(modal) {
    if (!modal) return;

    // Remove existing warning if any
    const existingWarning = modal.querySelector(this.selectors.noStoreWarning);
    if (existingWarning) {
      existingWarning.remove();
    }

    // Create warning element
    const warning = document.createElement('div');
    warning.className = this.selectors.noStoreWarning.substring(1);
    warning.style.cssText = 'padding: 1rem; margin: 1rem 0; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404;';
    warning.textContent = 'No stores found near your location. Please try a different location.';

    // Insert warning after the form
    const form = modal.querySelector(this.selectors.setStoreForm);
    if (form && form.parentNode) {
      form.parentNode.insertBefore(warning, form.nextSibling);
    }
  }

  async getCurrentLocation(inputElement) {
    if (!inputElement instanceof HTMLInputElement) return;

    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    this.backupAndClearSelectedPlace();

    const isGoogleMapsReady = await this.waitForGoogleMaps();
    if (!isGoogleMapsReady) {
      console.warn('Google Maps API not available');
      return;
    }

    const modal = this.getModal('set-store-modal');
    const originalPlaceholder = inputElement.placeholder;
    inputElement.placeholder = 'Getting your location...';
    inputElement.disabled = true;

    try {
      const position = await this.getGeolocationPosition();
      const { latitude: lat, longitude: lng } = position.coords;

      const geocoder = new window.google.maps.Geocoder();
      const geocodeResult = await this.reverseGeocodeWithGoogleMaps(geocoder, lat, lng);

      const countryCode = this.extractCountryCode(geocodeResult);

      // Only update if user hasn't manually entered a value
      if (!this.userHasEnteredValue) {
        inputElement.value = geocodeResult.formatted_address;
      }
      inputElement.disabled = false;
      inputElement.placeholder = originalPlaceholder;

      this.lastSearchLocation = geocodeResult.formatted_address;

      // Search stores and filter by lat/lng
      await this.searchStoresWithLatLng(lat, lng);
    } catch (error) {
      console.error('Error getting location:', error);
      inputElement.disabled = false;
      inputElement.placeholder = originalPlaceholder;
      this.handleGeolocationError(error);
    }
  }

  /**
   * Search stores and check for data
   */
  async searchStoresWithLatLng(lat, lng) {
    const modal = this.getModal('set-store-modal');
    if (!modal) return;

    const input = modal.querySelector(this.selectors.setStoreInput);
    const location = input instanceof HTMLInputElement ? input.value.trim() : '';

    if (!location) {
      console.warn('Please enter a postcode or suburb');
      return;
    }

    await this.performStoreSearch(location, modal, input, { lat, lng });
  }

  /**
   * Wait for Google Maps API to be ready
   */
  async waitForGoogleMaps() {
    if (typeof window.google !== 'undefined' && window.google.maps?.Geocoder) {
      return true;
    }

    const maxWait = 5000;
    const startTime = Date.now();
    while (Date.now() - startTime < maxWait) {
      if (typeof window.google !== 'undefined' && window.google.maps?.Geocoder) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return false;
  }

  /**
   * Get geolocation position
   */
  getGeolocationPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }

  /**
   * Reverse geocode using Google Maps Geocoder
   */
  reverseGeocodeWithGoogleMaps(geocoder, lat, lng) {
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results?.[0]) {
          resolve(results[0]);
        } else if (status === window.google.maps.GeocoderStatus.REQUEST_DENIED) {
          reject(new Error('REQUEST_DENIED'));
        } else if (status === window.google.maps.GeocoderStatus.OVER_QUERY_LIMIT) {
          reject(new Error('OVER_QUERY_LIMIT'));
        } else if (status === window.google.maps.GeocoderStatus.ZERO_RESULTS) {
          reject(new Error('ZERO_RESULTS'));
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }

  /**
   * Geocode address string to lat/lng using Google Maps Geocoder
   */
  geocodeAddressWithGoogleMaps(geocoder, address) {
    return new Promise((resolve, reject) => {
      geocoder.geocode({
        address: address,
        componentRestrictions: { country: 'au' }
      }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results?.[0]?.geometry?.location) {
          resolve({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
        } else if (status === window.google.maps.GeocoderStatus.REQUEST_DENIED) {
          reject(new Error('REQUEST_DENIED'));
        } else if (status === window.google.maps.GeocoderStatus.OVER_QUERY_LIMIT) {
          reject(new Error('OVER_QUERY_LIMIT'));
        } else if (status === window.google.maps.GeocoderStatus.ZERO_RESULTS) {
          reject(new Error('ZERO_RESULTS'));
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }

  /**
   * Extract country code from geocode result
   */
  extractCountryCode(geocodeResult) {
    const countryComponent = geocodeResult.address_components?.find(
      component => component.types?.includes('country')
    );
    return countryComponent?.short_name || '';
  }

  /**
   * Handle geolocation errors
   */
  handleGeolocationError(error) {
    if (error.code === 1 || error.code === error.PERMISSION_DENIED) {
      console.warn('Location permission denied');
    } else if (error.code === 2 || error.code === error.POSITION_UNAVAILABLE) {
      console.warn('Location information unavailable');
    } else if (error.code === 3 || error.code === error.TIMEOUT) {
      console.warn('Location request timeout');
    } else if (error.message?.includes('REQUEST_DENIED')) {
      console.error('Google Maps API key error (REQUEST_DENIED)');
    } else if (error.message?.includes('OVER_QUERY_LIMIT')) {
      console.warn('Google Maps API quota exceeded');
    } else {
      console.warn('Unknown geolocation error:', error.message);
    }
  }


  /**
   * Search stores by location
   */
  async searchStores(selectedPlaceLatLng) {
    const modal = this.getModal('set-store-modal');
    if (!modal) return;

    const input = modal.querySelector(this.selectors.setStoreInput);
    if (!(input instanceof HTMLInputElement)) return;

    const suggestions = this.querySelector(this.selectors.storeSuggestions) || document.querySelector(this.selectors.storeSuggestions);
    let location = input.value.trim();
    let latLng = selectedPlaceLatLng;

    // If suggestions are currently shown, automatically select the first option
    if (suggestions && suggestions.classList.contains('has-suggestions')) {
      const firstSuggestion = suggestions.querySelector(this.selectors.suggestionItem);
      if (firstSuggestion) {
        const selectedValue = firstSuggestion.textContent.trim();
        input.value = selectedValue;
        location = selectedValue;
        latLng = null; // Clear lat/lng so that it will geocode the selected suggestion text

        // Clear suggestions
        suggestions.innerHTML = '';
        suggestions.classList.remove('has-suggestions');
      }
    }

    if (!location) {
      console.warn('Please enter a postcode or suburb');
      return;
    }

    await this.performStoreSearch(location, modal, input, latLng);
  }

  /**
   * Perform store search - consolidated logic
   */
  async performStoreSearch(location, modal, input, selectedPlaceLatLng) {
    const findButton = modal.querySelector(this.selectors.findButton);
    let originalButtonContent = '';
    if (findButton instanceof HTMLButtonElement) {
      originalButtonContent = findButton.innerHTML;
      findButton.disabled = true;
      findButton.innerHTML = this.getLoadingSpinner();
    }

    try {
      const result = await this.findStoresByLocation(location, selectedPlaceLatLng);
      let stores = result.stores || [];
      const geocodedLocation = result.geocodedLocation;

      // Combine geocoded store with nearest stores (avoiding duplicates)
      let allFoundStores = [...stores];
      if (geocodedLocation) {
        const geocodedStore = this.convertGeocodedLocationToStore(geocodedLocation);
        if (!allFoundStores.some(s => s.id === geocodedStore.id)) {
          allFoundStores = [geocodedStore, ...allFoundStores];
        }
      }

      // Deduplicate stores by ID
      const seenIds = new Set();
      allFoundStores = allFoundStores.filter(store => {
        if (!store || !store.id) return false;
        const id = String(store.id);
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });

      this.cachedSearchResults = { stores: allFoundStores, geocodedLocation };
      this.showAllStores = false;

      // Perform search error handling
      if (allFoundStores.length === 0) {
        await this.saveStoreResultsToDatabase(location, { stores: [], geocodedLocation: null });
        this.hideStoreListElements(modal);
        this.showNoStoreWarning(modal);
        return;
      }

      const existingWarning = modal.querySelector(this.selectors.noStoreWarning);
      if (existingWarning) {
        existingWarning.remove();
      }

      if (input instanceof HTMLInputElement) {
        input.value = location;
      }

      const searchLocation = (typeof location === 'string' ? location.trim() : '') || geocodedLocation?.name || '';
      this.lastSearchLocation = searchLocation;

      const modalContent = modal.querySelector(this.selectors.setStoreModalContent);
      if (modalContent instanceof HTMLElement) {
        modalContent.classList.remove('show-all-stores-view');
      }

      if (!this.showAllStores) {
        const storesToShow = allFoundStores.slice(0, 3);
        await this.displayClosestStore(storesToShow);
      } else {
        const currentStore = await this.loadSelectedStore();
        this.displayStoreList(allFoundStores, currentStore);
      }
    } catch (error) {
      console.error('Error finding stores:', error);
      this.showNoStoreWarning(modal);
    } finally {
      if (findButton instanceof HTMLButtonElement) {
        findButton.disabled = false;
        if (originalButtonContent) {
          findButton.innerHTML = originalButtonContent;
        } else {
          findButton.textContent = 'FIND MY STORE';
        }
      }
    }
  }

  /**
   * Helper: Hide store list elements
   */
  hideStoreListElements(modal) {
    const closestSection = modal.querySelector(this.selectors.closestStore);
    const storeList = modal.querySelector(this.selectors.storeList);
    const setButton = modal.querySelector(this.selectors.setButton);
    const selectButton = modal.querySelector(this.selectors.selectButton);
    const actions = modal.querySelector(this.selectors.storeDetailsActions);

    if (closestSection instanceof HTMLElement) closestSection.style.display = 'none';
    if (storeList instanceof HTMLElement) storeList.style.display = 'none';
    if (setButton instanceof HTMLElement) setButton.style.display = 'none';
    if (selectButton instanceof HTMLElement) selectButton.style.display = 'none';
    if (actions instanceof HTMLElement) actions.style.display = 'none';
  }

  /**
   * Display closest store cards (up to 3 stores)
   */
  async displayClosestStore(storesInput) {
    const modal = this.getModal('set-store-modal');
    if (!modal) return;

    const stores = Array.isArray(storesInput) ? storesInput : [storesInput];
    const validStores = stores.filter(s => s && s.address);

    if (validStores.length === 0) {
      this.hideElements([this.selectors.setStoreModalActions, this.selectors.closestStore, this.selectors.storeList]);
      this.showNoStoreWarning(modal);
      return;
    }

    const closestSection = modal.querySelector(this.selectors.closestStore);
    const storeList = modal.querySelector(this.selectors.storeList);
    const findButton = modal.querySelector(this.selectors.findButton);
    const setButton = modal.querySelector(this.selectors.setButton);
    const selectButton = modal.querySelector(this.selectors.selectButton);
    const actions = modal.querySelector(this.selectors.storeDetailsActions);

    const currentStore = await this.loadSelectedStore();

    if (closestSection instanceof HTMLElement) {
      let selectedIndex = 0;
      if (currentStore && currentStore.id) {
        const foundIdx = validStores.findIndex(s => String(s.id) === String(currentStore.id));
        if (foundIdx >= 0) selectedIndex = foundIdx;
      }

      closestSection.innerHTML = validStores.map((store, index) => {
        const isSelected = index === selectedIndex;
        const isFirst = index === 0;
        const storeKey = `closest_store_${store.id}_${index}`;
        this.storeDataMap.set(storeKey, store);

        return `
          <div class="closest-store closest-store--card ${isSelected ? 'closest-store--selected set-store__closest-store--selected' : ''}"
               data-store-id="${store.id}"
               data-store-key="${storeKey}">
            <div class="closest-store__check-icon" style="display: none;">
              <span class="svg-wrapper">${this.getCheckmarkIcon()}</span>
            </div>
            ${isFirst ? `
              <div class="closest-store__header">
                <h3 class="closest-store__title">Your Closest Store</h3>
              </div>
            ` : ''}
            <div class="closest-store__info">
              <div class="closest-store__name-row">
                <span class="closest-store__name">${this.escapeHtml(store.name)}</span>
                <span class="closest-store__distance">${store.distance || 0} km</span>
              </div>
              <div class="closest-store__address">${this.escapeHtml(store.address)}</div>
              <button type="button" class="button button--link closest-store__change-link">Change Store
                <span class="svg-wrapper">${this.getArrowRightIcon()}</span>
              </button>
            </div>
          </div>
        `;
      }).join('');

      closestSection.style.display = 'block';
      const initialSelectedStore = validStores[selectedIndex];
      const initialKey = `closest_store_${initialSelectedStore.id}_${selectedIndex}`;
      closestSection.dataset.storeId = initialSelectedStore.id;
      closestSection.dataset.storeKey = initialKey;
      this.selectedClosestStore = initialSelectedStore;
      closestSection.classList.remove('set-store__closest-store--selected');
      closestSection.dataset.selected = 'true';

      // Attach event listeners to Change Store buttons and Card clicks
      closestSection.querySelectorAll('.closest-store--card').forEach((card) => {
        const changeLink = card.querySelector(this.selectors.closestStoreChangeLink);
        if (changeLink) {
          changeLink.addEventListener('click', async (e) => {
            e.stopPropagation();
            this.showAllStores = true;
            this.hideNoStoreWarning();

            const modalContent = modal.querySelector(this.selectors.setStoreModalContent);
            if (modalContent instanceof HTMLElement) {
              modalContent.classList.add('show-all-stores-view');
            }

            if (this.cachedSearchResults?.stores?.length || this.cachedSearchResults?.geocodedLocation) {
              let allStores = this.cachedSearchResults.stores || [];
              if (this.cachedSearchResults.geocodedLocation) {
                const geocodedStore = this.convertGeocodedLocationToStore(this.cachedSearchResults.geocodedLocation);
                if (!allStores.some(s => s.id === geocodedStore.id)) {
                  allStores = [geocodedStore, ...allStores];
                }
              }
              const seenIds = new Set();
              allStores = allStores.filter(s => {
                if (!s || !s.id) return false;
                const id = String(s.id);
                if (seenIds.has(id)) return false;
                seenIds.add(id);
                return true;
              });
              const curStore = await this.loadSelectedStore();
              this.displayStoreList(allStores, curStore);
            }
          });
        }

        card.addEventListener('click', (e) => {
          if (e.target.closest(this.selectors.closestStoreChangeLink)) return;
          closestSection.querySelectorAll('.closest-store--card').forEach(c => {
            c.classList.remove('set-store__closest-store--selected');
            c.classList.remove('closest-store--selected');
          });
          card.classList.add('set-store__closest-store--selected');
          card.classList.add('closest-store--selected');
          closestSection.dataset.storeId = card.dataset.storeId;
          const storeKey = card.dataset.storeKey;
          closestSection.dataset.storeKey = storeKey;
          if (storeKey && this.storeDataMap.has(storeKey)) {
            this.selectedClosestStore = this.storeDataMap.get(storeKey);
          }
        });
      });
    }

    // Hide store list, show set button, hide select button
    if (storeList instanceof HTMLElement) storeList.style.display = 'none';
    if (findButton instanceof HTMLElement) findButton.style.display = 'block';
    if (setButton instanceof HTMLElement) setButton.style.display = 'block';
    if (selectButton instanceof HTMLElement) selectButton.style.display = 'none';

    // Show actions
    if (actions instanceof HTMLElement) actions.style.display = 'flex';
  }

  /**
   * Display store list (multiple results)
   */
  async displayStoreList(stores, currentStore = null) {
    const modal = this.getModal('set-store-modal');
    if (!modal) return;

    // Deduplicate stores by ID to ensure no duplicate stores are displayed
    if (Array.isArray(stores)) {
      const seenIds = new Set();
      stores = stores.filter(store => {
        if (!store || !store.id) return false;
        const id = String(store.id);
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });
    }

    const closestSection = modal.querySelector(this.selectors.closestStore);
    const storeList = modal.querySelector(this.selectors.storeList);
    const findButton = modal.querySelector(this.selectors.findButton);
    const selectButton = modal.querySelector(this.selectors.selectButton);
    const changeLink = modal.querySelector(this.selectors.closestStoreChangeLink);
    const actions = modal.querySelector(this.selectors.storeDetailsActions);

    // Load current store if not provided
    if (!currentStore) {
      currentStore = await this.loadSelectedStore();
    }

    // If closest store section is selected, remove that store from the list
    // if (
    //   closestSection instanceof HTMLElement &&
    //   closestSection.dataset.selected === 'true'
    // ) {
    //   const closestStoreId = closestSection.dataset.storeId || '';
    //   const currentStoreId = currentStore && currentStore.id ? String(currentStore.id) : '';
    //   const currentStoreName = currentStore?.name || '';
    //   const currentStoreAddress = currentStore?.address || '';

    //   stores = stores.filter((s) => {
    //     if (!s || typeof s !== 'object') return true;

    //     const sId = 'id' in s && s.id ? String(s.id) : '';
    //     const sameAsClosest = closestStoreId && sId === closestStoreId;
    //     const sameAsCurrent =
    //       currentStoreId && sId === currentStoreId ||
    //       (s.name === currentStoreName && s.address === currentStoreAddress);

    //     // Keep store only if it is not the closest/current one
    //     return !sameAsClosest && !sameAsCurrent;
    //   });
    // }

    if (storeList instanceof HTMLElement) {
      this.storeDataMap.clear();

      // Find index of current store to select it (after any filtering)
      let selectedIndex = 0;
      if (currentStore && typeof currentStore === 'object' && 'id' in currentStore && currentStore.id) {
        const currentStoreId = String(currentStore.id);
        const currentStoreName = currentStore.name || '';
        const currentStoreAddress = currentStore.address || '';

        const foundIndex = stores.findIndex((s) => {
          if (s && typeof s === 'object' && 'id' in s) {
            return String(s.id) === currentStoreId ||
              (s.name === currentStoreName && s.address === currentStoreAddress);
          }
          return false;
        });
        if (foundIndex >= 0) {
          selectedIndex = foundIndex;
        }
      }

      storeList.innerHTML = stores.map((store, index) => {
        const storeKey = `store_${store.id}_${index}`;
        this.storeDataMap.set(storeKey, store);
        const isSelected = index === selectedIndex;

        return `
        <label class="set-store__store-item ${isSelected ? 'set-store__store-item--selected' : ''}"
               data-store-id="${store.id}" data-store-key="${storeKey}">
          <input type="radio" name="store" value="${store.id}"
                 data-store-key="${storeKey}" ${isSelected ? 'checked' : ''} style="display: none;">
          <div class="set-store__store-check-icon">
            <span class="svg-wrapper">${isSelected ? this.getCheckmarkIcon() : this.getUncheckIcon()}</span>
          </div>
          <div class="set-store__store-info">
            <div class="set-store__store-header">
              <span class="set-store__store-name">${this.escapeHtml(store.name)}</span>
              <span class="set-store__store-distance">${store.distance || 0} km</span>
            </div>
            <div class="set-store__store-address">${this.escapeHtml(store.address)}</div>
          </div>
        </label>
      `;
      }).join('');

      storeList.querySelectorAll(this.selectors.storeItem).forEach(item => {
        item.addEventListener('click', (e) => {
          const target = e.target;
          if (target instanceof HTMLElement && !(target instanceof HTMLInputElement && target.type === 'radio')) {
            const radio = item.querySelector(this.selectors.storeRadioInput);
            if (radio instanceof HTMLInputElement) {
              radio.checked = true;

              // Update all store items - set to uncheck icon
              storeList.querySelectorAll(this.selectors.storeItem).forEach(i => {
                i.classList.remove('set-store__store-item--selected');
                const checkIconWrapper = i.querySelector(`${this.selectors.storeCheckIcon} ${this.selectors.svgWrapper}`);
                if (checkIconWrapper instanceof HTMLElement) {
                  checkIconWrapper.innerHTML = this.getUncheckIcon();
                }
              });

              // Select clicked item - set to check icon
              item.classList.add('set-store__store-item--selected');
              const checkIconWrapper = item.querySelector(`${this.selectors.storeCheckIcon} ${this.selectors.svgWrapper}`);
              if (checkIconWrapper instanceof HTMLElement) {
                checkIconWrapper.innerHTML = this.getCheckmarkIcon();
              }

              // Unselect closest store section if it exists - show uncheck icon
              if (closestSection instanceof HTMLElement) {
                closestSection.classList.remove('set-store__closest-store--selected');
                closestSection.dataset.selected = 'false';
                const closestCheckIcon = closestSection.querySelector(this.selectors.closestStoreCheckIcon);
                if (closestCheckIcon instanceof HTMLElement) {
                  closestCheckIcon.style.display = 'flex';
                  const iconWrapper = closestCheckIcon.querySelector(this.selectors.svgWrapper);
                  if (iconWrapper) {
                    iconWrapper.innerHTML = this.getUncheckIcon();
                  }
                }
              }
            }
          }
        });
      });

      storeList.style.display = 'block';
      closestSection.style.display = 'none';
    }

    // Hide change link when displaying store list
    if (changeLink instanceof HTMLElement) {
      changeLink.style.display = 'none';
    }

    // Keep closest section visible if showAllStores is true (after clicking Change Store)
    // Hide it only if we're showing all stores from the beginning
    if (!this.showAllStores && closestSection instanceof HTMLElement) {
      closestSection.style.display = 'none';
    }

    // When store list is displayed, closest store should be selected by default
    // unless a store from the list is already selected
    const isStoreListVisible = storeList instanceof HTMLElement && storeList.style.display === 'block';
    const isClosestSectionVisible = closestSection instanceof HTMLElement && closestSection.style.display !== 'none';

    if (isStoreListVisible && isClosestSectionVisible) {
      const closestStoreId = closestSection.dataset.storeId;
      const checkIcon = closestSection.querySelector(this.selectors.closestStoreCheckIcon);

      // Default: select closest store when store list is displayed
      let shouldSelectClosest = true;

      // Check if current store is in the list (and not the closest store)
      if (currentStore && typeof currentStore === 'object' && 'id' in currentStore && closestStoreId) {
        const currentStoreId = String(currentStore.id);
        const currentStoreName = currentStore.name || '';
        const currentStoreAddress = currentStore.address || '';

        // Check if current store matches a store in the list (not closest store)
        const currentStoreInList = stores.some((s) => {
          if (s && typeof s === 'object' && 'id' in s) {
            const matchesId = String(s.id) === currentStoreId;
            const matchesNameAndAddress = s.name === currentStoreName && s.address === currentStoreAddress;
            // Don't select closest if current store is in the list and not the closest
            if ((matchesId || matchesNameAndAddress) && String(s.id) !== closestStoreId) {
              return true;
            }
          }
          return false;
        });

        if (currentStoreInList) {
          shouldSelectClosest = false;
        }
      }

      if (shouldSelectClosest) {
        closestSection.classList.add('set-store__closest-store--selected');
        closestSection.dataset.selected = 'true';
        if (checkIcon instanceof HTMLElement) {
          checkIcon.style.display = 'flex';
          const iconWrapper = checkIcon.querySelector(this.selectors.svgWrapper);
          if (iconWrapper) {
            iconWrapper.innerHTML = this.getCheckmarkIcon();
          }
        }

        // Uncheck all stores in the list
        const allRadios = modal.querySelectorAll(this.selectors.storeRadio);
        allRadios.forEach(radio => {
          if (radio instanceof HTMLInputElement) {
            radio.checked = false;
          }
        });

        // Remove selected class from all store items and set to uncheck icons
        const allStoreItems = modal.querySelectorAll(this.selectors.storeItem);
        allStoreItems.forEach(item => {
          item.classList.remove('set-store__store-item--selected');
          const itemCheckIconWrapper = item.querySelector(`${this.selectors.storeCheckIcon} ${this.selectors.svgWrapper}`);
          if (itemCheckIconWrapper instanceof HTMLElement) {
            itemCheckIconWrapper.innerHTML = this.getUncheckIcon();
          }
        });
      } else {
        // Current store is in the list, so don't select closest store - show uncheck icon
        closestSection.classList.remove('set-store__closest-store--selected');
        closestSection.dataset.selected = 'false';
        if (checkIcon instanceof HTMLElement) {
          checkIcon.style.display = 'flex';
          const iconWrapper = checkIcon.querySelector(this.selectors.svgWrapper);
          if (iconWrapper) {
            iconWrapper.innerHTML = this.getUncheckIcon();
          }
        }
      }

      // Add click handler to closest store section to allow selecting it again
      closestSection.addEventListener('click', (e) => {
        const target = e.target;
        if (target instanceof HTMLElement && !target.closest(this.selectors.closestStoreChangeLink)) {
          // Uncheck all stores in the list
          const allRadios = modal.querySelectorAll(this.selectors.storeRadio);
          allRadios.forEach(radio => {
            if (radio instanceof HTMLInputElement) {
              radio.checked = false;
            }
          });

          // Remove selected class from all store items and set to uncheck icons
          const allStoreItems = modal.querySelectorAll(this.selectors.storeItem);
          allStoreItems.forEach(item => {
            item.classList.remove('set-store__store-item--selected');
            const itemCheckIconWrapper = item.querySelector(`${this.selectors.storeCheckIcon} ${this.selectors.svgWrapper}`);
            if (itemCheckIconWrapper instanceof HTMLElement) {
              itemCheckIconWrapper.innerHTML = this.getUncheckIcon();
            }
          });

          // Select closest store section - show check icon
          closestSection.classList.add('set-store__closest-store--selected');
          closestSection.dataset.selected = 'true';
          const closestCheckIconEl = closestSection.querySelector(this.selectors.closestStoreCheckIcon);
          if (closestCheckIconEl instanceof HTMLElement) {
            closestCheckIconEl.style.display = 'flex';
            const iconWrapper = closestCheckIconEl.querySelector(this.selectors.svgWrapper);
            if (iconWrapper) {
              iconWrapper.innerHTML = this.getCheckmarkIcon();
            }
          }
        }
      });
    }

    // If closest store has data but is not selected, hide it
    if (closestSection instanceof HTMLElement && closestSection.dataset.selected !== 'true') {
      closestSection.style.display = 'none';
    }

    // Keep form and find button visible, show select button, hide set button
    if (findButton instanceof HTMLElement) findButton.style.display = 'block';
    if (selectButton instanceof HTMLElement) selectButton.style.display = 'block';

    // Show actions
    if (actions instanceof HTMLElement) actions.style.display = 'flex';

    const setButtonEl = modal.querySelector(this.selectors.setButton);
    if (setButtonEl instanceof HTMLElement) setButtonEl.style.display = 'none';

    this.storeListClasses();
  }

  storeListClasses() {
    // Show 5 items in store list if closest section is hidden, otherwise it will show 4 items and the user might not realize there are more stores to see
    const storeList = this.querySelector(this.selectors.storeList);
    if (!storeList) return;

    const isClosestSectionInvisible = this.querySelector(this.selectors.closestStore)?.style.display === 'none';
    if (isClosestSectionInvisible) {
      storeList.classList.add('show-5');
      storeList.classList.remove('show-4');
    } else {
      storeList.classList.add('show-4');
      storeList.classList.remove('show-5');
    }
  }

  /**
   * Save selected store
   */
  async saveSelectedStore() {
    const modal = this.getModal('set-store-modal');
    if (!modal) return;

    let storeId = null;
    let store = null;

    const closestSection = modal.querySelector(this.selectors.closestStore);
    const storeList = modal.querySelector(this.selectors.storeList);
    const isStoreListVisible = storeList instanceof HTMLElement && storeList.style.display === 'block';
    const isClosestVisible = closestSection instanceof HTMLElement && closestSection.style.display !== 'none';

    // Case 0: if store list is not visible but closest store is visible,
    // always prioritize data from closestSection / selected card.
    if (!isStoreListVisible && isClosestVisible) {
      const selectedCard = closestSection.querySelector('.closest-store.closest-store--selected') ||
                          closestSection.querySelector('.closest-store.set-store__closest-store--selected') ||
                          closestSection;
      storeId = selectedCard.dataset.storeId || closestSection.dataset.storeId || null;
      const storeKey = selectedCard.dataset.storeKey || closestSection.dataset.storeKey;
      if (storeKey && this.storeDataMap.has(storeKey)) {
        store = this.storeDataMap.get(storeKey);
      } else if (this.selectedClosestStore) {
        store = this.selectedClosestStore;
      }
    }

    // Priority 1: Check for selected radio button from store list
    const selectedRadio = modal.querySelector(this.selectors.storeRadioChecked);
    if (!store && selectedRadio instanceof HTMLInputElement) {
      storeId = selectedRadio.value;
      const storeKey = selectedRadio.dataset.storeKey;
      if (storeKey && this.storeDataMap.has(storeKey)) {
        store = this.storeDataMap.get(storeKey);
      } else {
        const storeItem = selectedRadio.closest('.set-store__store-item');
        if (storeItem instanceof HTMLElement) {
          const itemStoreKey = storeItem.dataset.storeKey;
          if (itemStoreKey && this.storeDataMap.has(itemStoreKey)) {
            store = this.storeDataMap.get(itemStoreKey);
          }
        }
      }
    }

    // Priority 2: Fallback from closest section or map
    if (!store && isClosestVisible) {
      const storeKey = closestSection.dataset.storeKey;
      if (storeKey && this.storeDataMap.has(storeKey)) {
        store = this.storeDataMap.get(storeKey);
        storeId = store?.id || closestSection.dataset.storeId;
      } else if (this.selectedClosestStore) {
        store = this.selectedClosestStore;
        storeId = store?.id;
      }
    }

    if (!storeId) {
      console.warn('Please select a store');
      return;
    }

    if (!store || !store.address) {
      console.warn('Store data not found');
      return;
    }
    const modalLocationInput = modal.querySelector(this.selectors.setStoreInput);
    const selectedLocationInput = modalLocationInput instanceof HTMLInputElement ? modalLocationInput.value.trim() : '';
    if (selectedLocationInput) {
      this.lastSearchLocation = selectedLocationInput;
      await this.saveLastSearchLocation(selectedLocationInput);
    }

    await this.saveSelectedStoreToDatabase(store);
    this.commitSelectedPlaceChange();
    this.closeSetStoreModal();
    await this.updateHeaderDisplay();
  }

  /**
   * Close Set Store Modal
   */
  closeSetStoreModal() {
    const modal = this.getModal('set-store-modal');
    if (modal) {
      // Remove no-animation class before closing so it has the closing animation
      const dialogElement = modal.querySelector('dialog');
      if (dialogElement instanceof HTMLElement) {
        dialogElement.classList.remove('no-animation');
      }

      const dialog = modal.querySelector(this.selectors.dialogComponent);
      if (dialog?.closeDialog && typeof dialog.closeDialog === 'function') {
        dialog.closeDialog();
      }
    }
  }

  /**
   * Filter out stores that have the same ID as geocodedLocation
   */
  filterStoresByGeocodedLocation(stores, geocodedLocation) {
    if (!geocodedLocation?.id || !Array.isArray(stores)) {
      return stores;
    }

    const extractId = (id) => {
      if (typeof id === 'string' && id.startsWith('gid://')) {
        const match = id.match(/\/(\d+)$/);
        return match ? match[1] : id;
      }
      return String(id);
    };

    const geocodedId = extractId(geocodedLocation.id);
    return stores.filter(store => {
      // Check both original API data and converted format
      const storeId = store?._apiData?.id || store?.id || null;
      if (!storeId) return true;
      const extractedStoreId = extractId(storeId);
      return extractedStoreId !== geocodedId;
    });
  }

  /**
   * Hide all elements matching the selector(s)
   * @param {string|string[]} selector - CSS selector or array of selectors
   */
  hideElements(selector) {
    const selectors = Array.isArray(selector) ? selector : [selector];
    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        if (el instanceof HTMLElement) el.style.display = 'none';
      });
    });
  }
  /**
   * Show all elements matching the selector(s)
   * @param {string|string[]} selector - CSS selector or array of selectors
   */
  showElements(selector) {
    const selectors = Array.isArray(selector) ? selector : [selector];
    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        if (el instanceof HTMLElement) el.style.display = '';
      });
    });
  }

  /**
   * Find stores by location (API call with 5-minute Hash comparison)
   */
  async findStoresByLocation(location, selectedPlaceLatLng, options = {}) {
    if (!location) {
      throw new Error('Location is required');
    }

    const suggestions = this.querySelector(this.selectors.storeSuggestions) || document.querySelector(this.selectors.storeSuggestions);
    if (suggestions) {
      suggestions.innerHTML = '';
      suggestions.classList.remove('has-suggestions');
    }

    // 1. Check IndexedDB cache (< 5 minutes TTL)
    if (!options.bypassCache) {
      const cached = await this.getCachedStoreResults(location, { checkTTL: true });
      if (cached) {
        return cached;
      }
    }

    // 2. Cache expired (> 5 minutes) or missing: fetch old record for Hash comparison
    const oldRecord = await this.getCachedStoreResults(location, { checkTTL: false });

    const settings = window.GOOGLE_MAP_SETTINGS || {};
    const maxDistance = Number(settings.radiusKm) || 200;

    try {
      const baseUrl = `${window.location.origin}/apps/service/shopify/nearest-store`;
      const params = new URLSearchParams();

      let latLng = selectedPlaceLatLng;
      if (!latLng) {
        const isGoogleMapsReady = await this.waitForGoogleMaps();
        if (isGoogleMapsReady) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            latLng = await this.geocodeAddressWithGoogleMaps(geocoder, location);
          } catch (error) {
            console.error('Error geocoding address in findStoresByLocation:', error);
          }
        }
      }

      if (latLng?.lat && latLng?.lng) {
        params.append('lat', String(latLng.lat));
        params.append('lng', String(latLng.lng));
        params.append('maxDistance', String(maxDistance));
        params.append('location', String(location));
      }
      const apiUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
      const response = await fetch(apiUrl, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        if (oldRecord) return oldRecord;
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      let stores = [];
      let geocodedLocation = null;

      if (data?.success && data?.data?.nearestStores && Array.isArray(data.data.nearestStores)) {
        const apiNearestStores = data.data.nearestStores;
        geocodedLocation = data.data.geocodedLocation || null;

        if (geocodedLocation && apiNearestStores.length) {
          const matchById = apiNearestStores.find(
            (s) => s?.id && geocodedLocation.id && s.id === geocodedLocation.id
          );
          const matchByStoreCode = !matchById && geocodedLocation.storeCode && apiNearestStores.find(
            (s) => s?.storeCode?.value && s.storeCode.value === geocodedLocation.storeCode?.value
          );
          const matchedStore = matchById || matchByStoreCode;

          if (matchedStore) {
            geocodedLocation = {
              ...geocodedLocation,
              priceBook: geocodedLocation.priceBook || matchedStore.priceBook || null,
              storeCode: geocodedLocation.storeCode || matchedStore.storeCode || null,
              storePage: matchedStore.storePage || null
            };
          }
        }

        const filteredApiStores = this.filterStoresByGeocodedLocation(
          apiNearestStores,
          geocodedLocation
        );

        stores = apiNearestStores.map(store => this.convertApiStoreToInternalFormat(store));

        if (stores.length === 0 && !geocodedLocation) {
          await this.saveStoreResultsToDatabase(location, { stores: [], geocodedLocation: null });
          return { stores: [], geocodedLocation: null };
        }
      } else if (data?.data?.nearestStore) {
        const nearestStore = data.data.nearestStore.nearestStore || data.data.nearestStore.defaultStore;
        if (nearestStore) {
          stores = [this.convertApiStoreToInternalFormat(nearestStore)];
        }
        geocodedLocation = data.data.geocodedLocation || null;
      } else if (data?.stores && Array.isArray(data.stores)) {
        stores = data.stores.map(store => this.convertApiStoreToInternalFormat(store));
      } else if (data?.data && Array.isArray(data.data)) {
        stores = data.data.map(store => this.convertApiStoreToInternalFormat(store));
      } else if (data?.nearestStore) {
        stores = [this.convertApiStoreToInternalFormat(data.nearestStore)];
      }

      const freshResultsPayload = { stores, geocodedLocation };
      const newHash = await this.computeHash(freshResultsPayload);
      const oldHash = oldRecord?.hash || '';

      // Hash comparison for storeResults
      if (!oldRecord || oldHash !== newHash) {
        // Hash is DIFFERENT: Save new data + hash into IndexedDB & notify UI
        await this.saveStoreResultsToDatabase(location, freshResultsPayload, newHash);
        window.dispatchEvent(new CustomEvent('store-results-updated', {
          detail: { location, results: freshResultsPayload, hash: newHash }
        }));
      } else {
        // Hash is SAME: Update timestamp in IndexedDB without UI flicker
        await this.saveStoreResultsToDatabase(location, freshResultsPayload, newHash);
      }

      // Hash comparison for selectedStore
      if (this.selectedStore) {
        const matchingStore = geocodedLocation?.id === this.selectedStore.id
          ? this.convertGeocodedLocationToStore(geocodedLocation)
          : stores.find(s => s.id === this.selectedStore.id || s._apiData?.id === this.selectedStore._apiData?.id);

        if (matchingStore) {
          const newStoreHash = await this.computeHash(matchingStore);
          const currentStoreRecord = await new Promise((resolve) => {
            this.getDatabase().then(db => {
              if (!db) return resolve(null);
              const tx = db.transaction(['selectedStore'], 'readonly');
              const req = tx.objectStore('selectedStore').get(StoreLocatorComponent.PREFERRED_STORE_KEY);
              req.onsuccess = () => resolve(req.result);
              req.onerror = () => resolve(null);
            });
          });
          const oldStoreHash = currentStoreRecord?.hash || '';

          if (!currentStoreRecord || oldStoreHash !== newStoreHash) {
            console.log('[StoreLocator] selectedStore Hash DIFFERENT -> Updating IndexedDB selectedStore & Header UI');
            await this.saveSelectedStoreToDatabase(matchingStore);
            await this.updateHeaderDisplay();
          } else {
            // Hash is SAME: Update timestamp
            await this.saveSelectedStoreToDatabase(matchingStore);
          }
        }
      }

      return { stores, geocodedLocation, hash: newHash };
    } catch (err) {
      console.error('Fetch nearest store error:', err);
      if (oldRecord) return oldRecord;
      throw err;
    }
  }

  /**
   * Save store search results to IndexedDB
   */
  async saveStoreResultsToDatabase(location, results, hashVal = null) {
    if (!location) return;
    try {
      const db = await this.getDatabase();
      const hash = hashVal || await this.computeHash(results);
      if (db) {
        return new Promise((resolve) => {
          const transaction = db.transaction(['storeResults'], 'readwrite');
          const objectStore = transaction.objectStore('storeResults');
          const expiresAt = Date.now() + (30 * 24 * 60 * 60 * 1000); // 30 days

          const now = Date.now();
          const data = {
            location: location,
            stores: results.stores || [],
            geocodedLocation: results.geocodedLocation || null,
            hash: hash,
            timestamp: now,
            expiresAt: expiresAt
          };

          const request = objectStore.put(data);

          request.onsuccess = () => {
            resolve();
          };

          request.onerror = () => {
            console.error('Error saving store results:', request.error);
            resolve();
          };
        });
      }
    } catch (e) {
      console.error('Error saving store results:', e);
    }
  }

  /**
   * Get cached store results from IndexedDB (with 5-minute TTL option)
   */
  async getCachedStoreResults(location, options = {}) {
    try {
      const db = await this.getDatabase();
      if (db) {
        return new Promise((resolve) => {
          const transaction = db.transaction(['storeResults'], 'readonly');
          const objectStore = transaction.objectStore('storeResults');
          const request = objectStore.get(location);

          request.onsuccess = () => {
            const result = request.result;
            if (result) {
              const timestamp = result.timestamp ?? 0;
              const cacheAge = Date.now() - timestamp;
              const checkTTL = options.checkTTL !== false;
              const isExpired = cacheAge > StoreLocatorComponent.CACHE_TTL_MS;

              if (!checkTTL || !isExpired) {
                let stores = result.stores || [];
                const geocodedLocation = result.geocodedLocation || null;

                resolve({
                  location: result.location,
                  stores: stores,
                  geocodedLocation: geocodedLocation,
                  hash: result.hash || '',
                  timestamp: timestamp,
                  isExpired: isExpired
                });
              } else {
                resolve(null);
              }
            } else {
              resolve(null);
            }
          };

          request.onerror = () => {
            console.error('Error reading cached results:', request.error);
            resolve(null);
          };
        });
      }
      return null;
    } catch (e) {
      console.error('Error getting cached results:', e);
      return null;
    }
  }

  /**
   * Get most recent cached store results
   */
  async getMostRecentCachedResults() {
    try {
      const db = await this.getDatabase();
      if (db) {
        return new Promise((resolve) => {
          const transaction = db.transaction(['storeResults'], 'readonly');
          const objectStore = transaction.objectStore('storeResults');
          const request = objectStore.getAll();

          request.onsuccess = () => {
            const results = request.result;
            if (results?.length) {
              results.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

              const maxAge = 60 * 60 * 1000;
              for (const result of results) {
                const ts = result.timestamp ?? 0;
                const cacheAge = Date.now() - ts;
                if (cacheAge < maxAge && result.stores?.length) {
                  resolve({
                    location: result.location,
                    stores: result.stores,
                    geocodedLocation: result.geocodedLocation || null
                  });
                  return;
                }
              }
            }
            resolve(null);
          };

          request.onerror = () => {
            console.error('Error reading all cached results:', request.error);
            resolve(null);
          };
        });
      }
      return null;
    } catch (e) {
      console.error('Error getting all cached results:', e);
      return null;
    }
  }

  /**
   * Save last search location
   */
  async saveLastSearchLocation(location) {
    try {
      const db = await this.getDatabase();
      if (db) {
        return new Promise((resolve) => {
          const transaction = db.transaction(['selectedStore'], 'readwrite');
          const objectStore = transaction.objectStore('selectedStore');

          const request = objectStore.put({
            id: 'lastSearch',
            location: location,
            timestamp: Date.now()
          });

          request.onsuccess = () => resolve();
          request.onerror = () => {
            console.error('Error saving last search location:', request.error);
            resolve();
          };
        });
      }
    } catch (e) {
      console.error('Error saving last search location:', e);
    }
  }

  /**
   * Get last search location
   */
  async getLastSearchLocation() {
    try {
      const db = await this.getDatabase();
      if (db) {
        return new Promise((resolve) => {
          const transaction = db.transaction(['selectedStore'], 'readonly');
          const objectStore = transaction.objectStore('selectedStore');
          const request = objectStore.get('lastSearch');

          request.onsuccess = () => {
            resolve(request.result?.location || null);
          };

          request.onerror = () => {
            console.error('Error reading last search location:', request.error);
            resolve(null);
          };
        });
      }
      return null;
    } catch (e) {
      console.error('Error getting last search location:', e);
      return null;
    }
  }

  /**
   * Delete selected store when expired (30 days)
   */
  async deleteSelectedStore() {
    try {
      const db = await this.getDatabase();
      if (db) {
        return new Promise((resolve) => {
          const transaction = db.transaction(['selectedStore'], 'readwrite');
          const objectStore = transaction.objectStore('selectedStore');
          const request = objectStore.delete(StoreLocatorComponent.PREFERRED_STORE_KEY);

          request.onsuccess = () => {
            this.selectedStore = null;
            this.updateHeaderDisplay();
            resolve();
          };

          request.onerror = () => {
            console.error('Error deleting selected store:', request.error);
            resolve();
          };
        });
      }
    } catch (e) {
      console.error('Error deleting selected store:', e);
    }
  }

  /**
   * Convert API store format to internal format
   */
  convertApiStoreToInternalFormat(apiStore) {
    let storeId = apiStore.id || apiStore.storeId || String(Date.now());
    if (typeof storeId === 'string' && storeId.startsWith('gid://')) {
      const match = storeId.match(/\/(\d+)$/);
      storeId = match ? match[1] : storeId;
    }

    let addressString = '';
    if (apiStore.address) {
      if (apiStore.address.formatted && Array.isArray(apiStore.address.formatted)) {
        addressString = apiStore.address.formatted.join(', ');
      } else {
        const parts = [];
        if (apiStore.address.address1) parts.push(apiStore.address.address1);
        if (apiStore.address.address2) parts.push(apiStore.address.address2);
        if (apiStore.address.city) parts.push(apiStore.address.city);
        if (apiStore.address.province) parts.push(apiStore.address.province);
        if (apiStore.address.zip) parts.push(apiStore.address.zip);
        if (apiStore.address.country) parts.push(apiStore.address.country);
        addressString = parts.join(', ');
      }
    } else if (typeof apiStore.address === 'string') {
      addressString = apiStore.address;
    }

    let addressForDetails = addressString;
    if (apiStore.address?.formatted && Array.isArray(apiStore.address.formatted)) {
      if (apiStore.address.formatted.length >= 2) {
        const street = apiStore.address.formatted[0];
        const cityState = apiStore.address.formatted[1];
        const country = apiStore.address.formatted[2] || '';
        addressForDetails = `${street} ${cityState}${country ? ', ' + country : ''}`;
      }
    }

    const postcode = apiStore.address?.zip || apiStore.postcode || apiStore.postalCode || '';
    const phone = apiStore.address?.phone || apiStore.phone || apiStore.phoneNumber || '';
    const email = apiStore?._apiData?.contactEmail?.value || apiStore.email || '';
    const distance = apiStore?.distanceKm || 0;

    // Parse opening hours from various possible sources
    let openingHours = null;
    const openingHoursSource =
      apiStore._apiData?.openingHours ||
      apiStore.openingHours;

    if (openingHoursSource) {
      if (openingHoursSource.value && typeof openingHoursSource.value === 'string') {
        // Try to parse as JSON array first
        openingHours = this.parseOpeningHoursJSON(openingHoursSource.value);
        // If that fails, try the old string format
        if (!openingHours) {
          openingHours = this.parseOpeningHoursString(openingHoursSource.value);
        }
      } else if (typeof openingHoursSource === 'object' && !openingHoursSource.value) {
        openingHours = openingHoursSource;
      }
    }

    return {
      id: String(storeId),
      name: apiStore.name || apiStore.storeName || 'Store',
      address: addressString,
      addressForDetails: addressForDetails,
      postcode: postcode,
      phone: phone,
      email: email,
      distance: distance,
      openingHours: openingHours,
      priceBook: apiStore.priceBook?.value || '',
      storeCode: apiStore.storeCode?.value || '',
      latitude: apiStore.address?.latitude || null,
      longitude: apiStore.address?.longitude || null,
      _apiData: apiStore
    };
  }

  /**
   * Convert geocoded location to store format
   */
  convertGeocodedLocationToStore(geocodedLocation) {
    const storeName = geocodedLocation.name ||
      (geocodedLocation.address ?
        `${geocodedLocation.address.city || ''} ${geocodedLocation.address.zip || ''}`.trim() :
        'Your Location');

    let addressString = '';
    if (geocodedLocation.address) {
      if (geocodedLocation.address.formatted && Array.isArray(geocodedLocation.address.formatted)) {
        addressString = geocodedLocation.address.formatted.join(', ');
      } else {
        const parts = [];
        if (geocodedLocation.address.address1) parts.push(geocodedLocation.address.address1);
        if (geocodedLocation.address.address2) parts.push(geocodedLocation.address.address2);
        if (geocodedLocation.address.city) parts.push(geocodedLocation.address.city);
        if (geocodedLocation.address.province) parts.push(geocodedLocation.address.province);
        if (geocodedLocation.address.zip) parts.push(geocodedLocation.address.zip);
        if (geocodedLocation.address.country) parts.push(geocodedLocation.address.country);
        addressString = parts.join(', ');
      }
    }

    let addressForDetails = addressString;
    if (geocodedLocation.address?.formatted && Array.isArray(geocodedLocation.address.formatted)) {
      if (geocodedLocation.address.formatted.length >= 2) {
        const street = geocodedLocation.address.formatted[0];
        const cityState = geocodedLocation.address.formatted[1];
        const country = geocodedLocation.address.formatted[2] || '';
        addressForDetails = `${street} ${cityState}${country ? ', ' + country : ''}`;
      }
    }

    // Parse opening hours from various possible sources
    let openingHours = null;
    const openingHoursSource =
      geocodedLocation._apiData?.openingHours ||
      geocodedLocation.openingHours;

    if (openingHoursSource) {
      if (openingHoursSource.value && typeof openingHoursSource.value === 'string') {
        // Try to parse as JSON array first
        openingHours = this.parseOpeningHoursJSON(openingHoursSource.value);
        // If that fails, try the old string format
        if (!openingHours) {
          openingHours = this.parseOpeningHoursString(openingHoursSource.value);
        }
      } else if (typeof openingHoursSource === 'object' && !openingHoursSource.value) {
        openingHours = openingHoursSource;
      }
    }

    let storeId = geocodedLocation.id || '';
    if (typeof storeId === 'string' && storeId.startsWith('gid://')) {
      const match = storeId.match(/\/(\d+)$/);
      storeId = match ? match[1] : storeId;
    }

    return {
      id: String(storeId),
      name: storeName,
      address: addressString,
      addressForDetails: addressForDetails,
      postcode: geocodedLocation.address?.zip || geocodedLocation.query || '',
      phone: geocodedLocation.address?.phone || '',
      email: geocodedLocation?._apiData?.contactEmail?.value || '',
      distance: geocodedLocation?.distanceKm || 0,
      openingHours: openingHours,
      storeCode: '',
      latitude: geocodedLocation.latitude || geocodedLocation.address?.latitude || null,
      longitude: geocodedLocation.longitude || geocodedLocation.address?.longitude || null,
      isGeocodedLocation: true,
      priceBook: geocodedLocation.priceBook?.value || '',
      _apiData: geocodedLocation
    };
  }

  /**
   * Parse opening hours from string format
   */
  parseOpeningHoursString(hoursString) {
    const hours = {};
    if (!hoursString || typeof hoursString !== 'string') return null;

    const lines = hoursString.split('\n').filter(line => line.trim());
    const dayMap = {
      'monday': 'monday',
      'tuesday': 'tuesday',
      'wednesday': 'wednesday',
      'thursday': 'thursday',
      'friday': 'friday',
      'saturday': 'saturday',
      'sunday': 'sunday'
    };

    lines.forEach(line => {
      const trimmed = line.trim();
      const match = trimmed.match(/^(\w+)\s+(.+)$/i);
      if (match) {
        const dayName = match[1].toLowerCase();
        const timeRange = match[2].trim();
        const mappedDay = dayMap[dayName];
        if (mappedDay) {
          hours[mappedDay] = timeRange;
        }
      }
    });

    return Object.keys(hours).length > 0 ? hours : null;
  }

  /**
   * Parse opening hours from JSON array format
   * Converts day_of_week (0=Sunday, 1=Monday, etc.) to day names
   * Formats times from 24h to 12h format
   */
  parseOpeningHoursJSON(hoursData) {
    if (!hoursData) return null;

    // If it's a string, try to parse it
    let hoursArray;
    if (typeof hoursData === 'string') {
      try {
        hoursArray = JSON.parse(hoursData);
      } catch (e) {
        console.error('Failed to parse opening hours JSON:', e);
        return null;
      }
    } else if (Array.isArray(hoursData)) {
      hoursArray = hoursData;
    } else {
      return null;
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const hours = {};

    hoursArray.forEach(dayData => {
      if (dayData.day_of_week !== undefined && dayData.start_time && dayData.end_time) {
        const dayName = dayNames[dayData.day_of_week];
        const startTime = this.formatTime(dayData.start_time);
        const endTime = this.formatTime(dayData.end_time);

        if (dayName && startTime && endTime) {
          hours[dayName] = `${startTime} - ${endTime}`;
        }
      }
    });

    return Object.keys(hours).length > 0 ? hours : null;
  }

  /**
   * Format time from 24h format (HH:MM:SS) to 12h format (h:mm am/pm)
   */
  formatTime(timeString) {
    if (!timeString) return null;

    const parts = timeString.split(':');
    if (parts.length < 2) return null;

    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];

    if (isNaN(hours)) return null;

    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    // Always show minutes for consistency
    return `${hours}:${minutes}${ampm}`;
  }



  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get arrow right icon SVG
   */
  getArrowRightIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M17.0788 12.5H5V11.5H17.0788L11.2865 5.70775L12 5L19 12L12 19L11.2865 18.2923L17.0788 12.5Z" fill="#054F8E"/>
    </svg>`;
  }

  /**
   * Get checkmark icon SVG (checked state)
   */
  getCheckmarkIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="icon-check">
      <mask id="mask0_9345_5653" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
        <rect width="24" height="24" fill="#D9D9D9"/>
      </mask>
      <g mask="url(#mask0_9345_5653)">
        <path d="M9.54982 17.3074L4.58057 12.3381L5.29407 11.6246L9.54982 15.8804L18.7056 6.72461L19.4191 7.43811L9.54982 17.3074Z" fill="currentColor"/>
      </g>
    </svg>`;
  }

  /**
   * Get uncheck icon SVG (unchecked state - empty circle)
   */
  getUncheckIcon() {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0.5C18.3513 0.5 23.5 5.64873 23.5 12C23.5 18.3513 18.3513 23.5 12 23.5C5.64873 23.5 0.5 18.3513 0.5 12C0.5 5.64873 5.64873 0.5 12 0.5Z" fill="white"/>
    <path d="M12 0.5C18.3513 0.5 23.5 5.64873 23.5 12C23.5 18.3513 18.3513 23.5 12 23.5C5.64873 23.5 0.5 18.3513 0.5 12C0.5 5.64873 5.64873 0.5 12 0.5Z" stroke="#E6E7E8"/>
    </svg>
  `;
  }

  /**
   * Get warning icon SVG
   */
  getWarningIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="icon-warning">
      <mask id="mask0_9345_11523" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
        <rect width="24" height="24" fill="#D9D9D9"/>
      </mask>
      <g mask="url(#mask0_9345_11523)">
        <path d="M2.73096 20L12.0002 4L21.2695 20H2.73096ZM12.0002 17.6155C12.1745 17.6155 12.3207 17.5565 12.4387 17.4385C12.5567 17.3205 12.6157 17.1743 12.6157 17C12.6157 16.8257 12.5567 16.6795 12.4387 16.5615C12.3207 16.4435 12.1745 16.3845 12.0002 16.3845C11.8259 16.3845 11.6797 16.4435 11.5617 16.5615C11.4437 16.6795 11.3847 16.8257 11.3847 17C11.3847 17.1743 11.4437 17.3205 11.5617 17.4385C11.6797 17.5565 11.8259 17.6155 12.0002 17.6155ZM11.5002 15.3845H12.5002V10.3845H11.5002V15.3845Z" fill="#F47920"/>
      </g>
    </svg>`;
  }

  getInfoIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.00325 18C7.75875 18 6.58867 17.7638 5.493 17.2915C4.3975 16.8192 3.4445 16.1782 2.634 15.3685C1.8235 14.5588 1.18192 13.6067 0.70925 12.512C0.236417 11.4175 0 10.2479 0 9.00325C0 7.75875 0.236167 6.58867 0.7085 5.493C1.18083 4.3975 1.82183 3.4445 2.6315 2.634C3.44117 1.8235 4.39333 1.18192 5.488 0.70925C6.5825 0.236417 7.75208 0 8.99675 0C10.2413 0 11.4113 0.236167 12.507 0.708501C13.6025 1.18083 14.5555 1.82183 15.366 2.6315C16.1765 3.44117 16.8181 4.39333 17.2908 5.488C17.7636 6.5825 18 7.75208 18 8.99675C18 10.2413 17.7638 11.4113 17.2915 12.507C16.8192 13.6025 16.1782 14.5555 15.3685 15.366C14.5588 16.1765 13.6067 16.8181 12.512 17.2908C11.4175 17.7636 10.2479 18 9.00325 18ZM8.5 13.5H9.5V8H8.5V13.5ZM9.43375 6.4C9.55492 6.282 9.6155 6.13583 9.6155 5.9615C9.6155 5.78717 9.5565 5.641 9.4385 5.523C9.3205 5.40517 9.17433 5.34625 9 5.34625C8.82567 5.34625 8.6795 5.40517 8.5615 5.523C8.4435 5.641 8.3845 5.78717 8.3845 5.9615C8.3845 6.13583 8.44508 6.282 8.56625 6.4C8.68742 6.518 8.832 6.577 9 6.577C9.168 6.577 9.31258 6.518 9.43375 6.4Z" fill="currentColor"/>
            </svg>
    `;
  }

  /**
   * Get loading spinner SVG
   */
  getLoadingSpinner() {
    return `<svg class="loading-spinner-svg" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; fill: none; stroke: currentColor; stroke-width: 3; stroke-linecap: round; color: inherit;">
      <circle cx="12" cy="12" r="10" stroke-dasharray="48" stroke-dashoffset="16">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
      </circle>
    </svg>`;
  }

  /**
   * Static helper: Open IndexedDB with proper initialization
   * Reuses the same logic as initializeDatabase() to avoid code duplication
   * @private
   */
  static async #openDatabase() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        resolve(null);
        return;
      }

      const request = indexedDB.open(StoreLocatorComponent.DB_NAME, StoreLocatorComponent.DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB open failed:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object stores if they don't exist (same logic as initializeDatabase)
        if (!db.objectStoreNames.contains('selectedStore')) {
          const store = db.createObjectStore('selectedStore', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('storeResults')) {
          const store = db.createObjectStore('storeResults', { keyPath: 'location' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Get preferred store for checkout attributes
   * Static method to be used from other components
   * @returns {Promise<Object|null>} The preferred store data or null if not found/expired
   */
  static async getPreferredStore() {
    try {
      // Try to get from StoreLocatorComponent instance first (most efficient)
      const component = document.querySelector('store-locator-component');
      if (component && component instanceof StoreLocatorComponent) {
        const store = await component.loadSelectedStore();
        return store;
      }

      // Fallback: directly access IndexedDB if component not found
      const db = await StoreLocatorComponent.#openDatabase();
      if (!db) {
        return null;
      }

      return new Promise((resolve) => {
        try {
          const transaction = db.transaction(['selectedStore'], 'readonly');
          const store = transaction.objectStore('selectedStore');
          const getRequest = store.get(StoreLocatorComponent.PREFERRED_STORE_KEY);

          getRequest.onsuccess = () => {
            const result = getRequest.result;
            if (result?.data) {
              // Check if expired (30 days)
              if (result.expiresAt && Date.now() > result.expiresAt) {
                resolve(null);
              } else {
                // Normalize data to match internal structure (same as loadSelectedStore)
                // Try to get component instance to use normalizeStoreData method
                const component = document.querySelector('store-locator-component');
                if (component && component instanceof StoreLocatorComponent && typeof component.normalizeStoreData === 'function') {
                  const normalized = component.normalizeStoreData(result.data);
                  resolve(normalized);
                } else {
                  // Fallback: return data as-is if normalization not available
                  resolve(result.data);
                }
              }
            } else {
              resolve(null);
            }
          };

          getRequest.onerror = () => {
            console.error('Error reading preferred store from IndexedDB:', getRequest.error);
            resolve(null);
          };
        } catch (error) {
          console.error('Error accessing IndexedDB transaction:', error);
          resolve(null);
        }
      });
    } catch (e) {
      console.error('Error getting preferred store:', e);
      return null;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('store-locator-component')) {
      customElements.define('store-locator-component', StoreLocatorComponent);
    }
  });
} else {
  if (!customElements.get('store-locator-component')) {
    customElements.define('store-locator-component', StoreLocatorComponent);
  }
}

// Expose getPreferredStore as a global utility function for easy access from other components
// This avoids circular dependencies and makes it accessible without imports
if (typeof window !== 'undefined') {
  window.getPreferredStore = StoreLocatorComponent.getPreferredStore.bind(StoreLocatorComponent);
}
